import React, { useState, useEffect, useMemo } from 'react';
import { getFirestore, collection, query, where, getDocs, doc, onSnapshot, updateDoc, arrayUnion, runTransaction } from 'firebase/firestore';
import { getAuth } from 'firebase/auth';
import { useNavigate } from 'react-router-dom';
import app, { programHeadAuth } from '../../firebase';
import DeleteConfirmationModal from '../common/DeleteConfirmationModal';

const db = getFirestore(app);
const auth = programHeadAuth;

const dayOptions = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'];
const timeSlots = ['08:00 - 10:00', '10:00 - 12:00', '13:00 - 15:00', '15:00 - 17:00'];

function normalizeDepartment(department, selectedDepartment) {
  return department.toLowerCase().includes('general') ? selectedDepartment : department;
}

function normalizeRoomName(roomName) {
  return (roomName || '').toString().trim().toLowerCase().replace(/\s+/g, ' ');
}

function normalizeDayName(day) {
  const prefix = (day || '').toString().trim().slice(0, 3).toLowerCase();
  const dayNames = {
    mon: 'monday',
    tue: 'tuesday',
    wed: 'wednesday',
    thu: 'thursday',
    fri: 'friday',
    sat: 'saturday',
    sun: 'sunday'
  };
  return dayNames[prefix] || '';
}

function getRoomBookingLockId(schoolYear, semester, roomName, day) {
  return [
    encodeURIComponent(schoolYear),
    encodeURIComponent(semester),
    encodeURIComponent(normalizeRoomName(roomName)),
    encodeURIComponent(normalizeDayName(day))
  ].join('__');
}

function parseCourseDuration(value) {
  if (typeof value === 'number') return value;
  if (!value) return 0;
  const text = String(value).trim().toLowerCase();
  const hours = text.match(/(\d+(?:\.\d+)?)\s*hrs?/);
  const minutes = text.match(/(\d+(?:\.\d+)?)\s*mins?/);
  if (hours || minutes) {
    return (hours ? Number(hours[1]) : 0) + (minutes ? Number(minutes[1]) / 60 : 0);
  }
  const numeric = Number(text);
  return Number.isFinite(numeric) ? numeric : 0;
}

function sortYearLevels(values) {
  const order = ['First Year', 'Second Year', 'Third Year', 'Fourth Year', 'Fifth Year'];
  return [...new Set(values)].sort((a, b) => order.indexOf(a) - order.indexOf(b));
}

export default function SchedulingManagement({ department, selectedDepartment, setSelectedDepartment, allDepartments, onBack, isGeneralEducation = false }) {
  const navigate = useNavigate();
  const [curriculumYears, setCurriculumYears] = useState([]);
  const [selectedCurriculumYear, setSelectedCurriculumYear] = useState('');
  const [selectedSemester, setSelectedSemester] = useState('First Semester');
  const [availableYearLevels, setAvailableYearLevels] = useState([]);
  const [selectedYearLevel, setSelectedYearLevel] = useState('');
  const [courses, setCourses] = useState([]);
  const [preferredDays, setPreferredDays] = useState({});
  const [professors, setProfessors] = useState([]);
  const [generatedSchedules, setGeneratedSchedules] = useState([]);
  const [savedSchedules, setSavedSchedules] = useState([]);
  const [roomBookingSchedules, setRoomBookingSchedules] = useState([]);
  const [roomBookingSchoolYear, setRoomBookingSchoolYear] = useState('');
  const [roomBookingsLoading, setRoomBookingsLoading] = useState(false);
  const [scheduleToDelete, setScheduleToDelete] = useState(null);
  const [generatedRowToRemove, setGeneratedRowToRemove] = useState(null);
  const [rooms, setRooms] = useState([]);
  const [roomsLoading, setRoomsLoading] = useState(true);
  const [roomsLoadError, setRoomsLoadError] = useState('');
  const [roomBookingsLoadError, setRoomBookingsLoadError] = useState('');
  const [savedFilterYearLevel, setSavedFilterYearLevel] = useState('');
  const [savedFilterSemester, setSavedFilterSemester] = useState('');
  const [savedFilterSection, setSavedFilterSection] = useState('');
  const [schoolYears, setSchoolYears] = useState(['2025-2026']);
  const [selectedSchoolYear, setSelectedSchoolYear] = useState('');
  const [newSchoolYear, setNewSchoolYear] = useState('');
  const [sections, setSections] = useState(['A', 'B', 'C']);
  const [selectedSection, setSelectedSection] = useState('');
  const [newSection, setNewSection] = useState('');
  const [statusMessage, setStatusMessage] = useState('');
  const [statusIsError, setStatusIsError] = useState(false);
  const [isSavingGenerated, setIsSavingGenerated] = useState(false);
  const [lunchBreakMap, setLunchBreakMap] = useState({});
  const [lastAssignedBreak, setLastAssignedBreak] = useState('');
  const isGeUser = isGeneralEducation || (department || '').toLowerCase().includes('general');

  const controlStyle = { height: 42, borderRadius: 10, border: '1px solid #ccd6dd', padding: '0 12px', fontSize: 14 };
  const controlFullWidthStyle = { ...controlStyle, width: '100%' };
  const actionButtonStyle = { height: 42, padding: '0 16px', borderRadius: 10, border: 'none', color: '#fff', cursor: 'pointer', fontWeight: 700 };
  const primaryButtonStyle = { ...actionButtonStyle, background: '#155c2b' };
  const secondaryButtonStyle = { ...actionButtonStyle, background: '#e67e22' };
  const disabledButtonStyle = { ...actionButtonStyle, background: '#b07c1a', cursor: 'not-allowed' };

  const defaultDayLimits = {
    Monday: { start: 7, end: 19 },
    Tuesday: { start: 7, end: 19 },
    Wednesday: { start: 7, end: 19 },
    Thursday: { start: 7, end: 19 },
    Friday: { start: 7, end: 12 }
  };

  const parseTimeToDecimal = (time) => {
    if (!time || typeof time !== 'string') return NaN;
    if (/\b(?:AM|PM)$/i.test(time.trim())) {
      return parseTime12HourToDecimal(time);
    }
    const [hourRaw, minuteRaw] = time.split(':');
    const hour = parseInt(hourRaw, 10);
    const minute = parseInt(minuteRaw, 10);
    if (Number.isNaN(hour) || Number.isNaN(minute)) return NaN;
    return hour + minute / 60;
  };

  const parseTime12HourToDecimal = (time) => {
    if (!time || typeof time !== 'string') return NaN;
    const match = /^([0-9]{1,2}):([0-9]{2})\s*(AM|PM)$/i.exec(time.trim());
    if (!match) return NaN;
    let hour = parseInt(match[1], 10);
    const minute = parseInt(match[2], 10);
    const period = match[3].toUpperCase();
    if (hour === 12) hour = 0;
    if (period === 'PM') hour += 12;
    return hour + minute / 60;
  };

  const parseTimeRangeFromString = (time) => {
    const match = /^([0-9]{1,2}:[0-9]{2}\s*(?:AM|PM))\s*-\s*([0-9]{1,2}:[0-9]{2}\s*(?:AM|PM))$/i.exec(time);
    if (!match) return null;
    const start = parseTime12HourToDecimal(match[1]);
    const end = parseTime12HourToDecimal(match[2]);
    if (Number.isNaN(start) || Number.isNaN(end)) return null;
    return { start, end };
  };

  const splitScheduleSegments = (value) => {
    if (!value) return [];
    if (typeof value !== 'string') return [String(value)];
    return value
      .split(/\r?\n|\s*\/\s*|\s*;\s*|\s*,\s*/)
      .map(segment => segment.trim())
      .filter(Boolean);
  };

  const getRowTimeBlocks = (row) => {
    if (!row?.day || !row?.time) return [];
    const dayLabels = splitScheduleSegments(row.day);
    const timeLabels = splitScheduleSegments(row.time);
    const blocks = [];
    const total = Math.max(dayLabels.length, timeLabels.length);

    for (let i = 0; i < total; i += 1) {
      const dayLabel = dayLabels[i] || dayLabels[0];
      const timeLabel = timeLabels[i] || timeLabels[0];
      const day = (dayLabel || '').split(' - ')[0]?.trim();
      const parsed = parseTimeRangeFromString(timeLabel);
      if (!day || !parsed) continue;
      blocks.push({ day, start: parsed.start, end: parsed.end });
    }
    return blocks;
  };

  const getRoomScheduleEntries = (row) => {
    if (!row?.room || !row?.day || !row?.time) return [];
    const roomLabels = splitScheduleSegments(row.room);
    const dayLabels = splitScheduleSegments(row.day);
    const timeLabels = splitScheduleSegments(row.time);
    const entries = [];
    const total = Math.max(roomLabels.length, dayLabels.length, timeLabels.length);

    for (let i = 0; i < total; i += 1) {
      const roomLabel = roomLabels[i] || roomLabels[0] || '';
      const dayLabel = dayLabels[i] || dayLabels[0] || '';
      const timeLabel = timeLabels[i] || timeLabels[0] || '';
      const day = (dayLabel || '').split(' - ')[0]?.trim();
      const parsed = parseTimeRangeFromString(timeLabel);
      if (!roomLabel || !day || !parsed) continue;
      entries.push({ room: roomLabel, day, start: parsed.start, end: parsed.end });
    }

    return entries;
  };

  const formatDecimalToClock = (decimalHour) => {
    if (typeof decimalHour !== 'number' || Number.isNaN(decimalHour)) return '';
    const hour = Math.floor(decimalHour);
    const minute = Math.round((decimalHour - hour) * 60);
    const hh = String(hour).padStart(2, '0');
    const mm = String(minute).padStart(2, '0');
    return `${hh}:${mm}`;
  };

  const buildProfessorBookingMap = (excludeScheduleId) => {
    const bookings = {};

    // Include saved schedules (global) so we consider already-saved assignments
    // Key by professorId if available, otherwise by name
    savedSchedules.forEach(row => {
      const key = row.professorId || row.professor;
      if (!key || key.toUpperCase?.() === 'TBA' || key.toUpperCase?.() === 'UNASSIGNED') return;
      const blocks = getRowTimeBlocks(row);
      if (blocks.length === 0) return;
      bookings[key] = bookings[key] || [];
      bookings[key].push(...blocks);
    });

    // Include professor documents' stored schedules
    professors.forEach(prof => {
      const profId = prof.id;
      const profSchedules = Array.isArray(prof.schedules) ? prof.schedules : [];
      profSchedules.forEach(s => {
        // If term is set on UI, only include matching term entries to reduce false conflicts
        if (selectedSchoolYear && s.schoolYear && s.schoolYear !== selectedSchoolYear) return;
        if (selectedSemester && s.semester && s.semester !== selectedSemester) return;
        const blocks = getRowTimeBlocks(s);
        if (blocks.length === 0) return;
        bookings[profId] = bookings[profId] || [];
        bookings[profId].push(...blocks);
      });
    });

    // Finally include generatedSchedules (excluding the current row if provided)
    generatedSchedules.forEach(row => {
      if (row.id === excludeScheduleId) return;
      const key = row.professorId || row.professor;
      if (!key || key.toUpperCase?.() === 'TBA' || key.toUpperCase?.() === 'UNASSIGNED') return;
      const blocks = getRowTimeBlocks(row);
      if (blocks.length === 0) return;
      bookings[key] = bookings[key] || [];
      bookings[key].push(...blocks);
    });

    return bookings;
  };

  const isProfessorFreeForRow = (professorIdOrName, row, bookingMap) => {
    if (!professorIdOrName) return false;
    const blocks = getRowTimeBlocks(row);
    if (blocks.length === 0) return true;
    const existing = bookingMap[professorIdOrName] || [];
    return blocks.every(block => !existing.some(slot => slot.day === block.day && isOverlap(block.start, block.end, slot.start, slot.end)));
  };

  const getProfessorOptionsForRow = (row) => {
    const course = { courseId: row.courseId, courseName: row.courseName };
    const assignedCandidates = getMatchingProfessorCandidates(course);
    const bookingMap = buildProfessorBookingMap(row.id);
    const currentProfessor = row.professor || '';
    const currentProfessorId = row.professorId || '';
    const assignedNames = new Set(assignedCandidates.map(prof => prof.name));

    const rowBlocks = getRowTimeBlocks(row);
    const options = assignedCandidates.map(prof => {
      const profKey = prof.id; // Use ID as primary key
      const isAvailable = isProfessorFreeForRow(profKey, row, bookingMap);
      let label = prof.name;
      let disabled = !isAvailable;
      if (!isAvailable) {
        // compute conflicting slots to show reason
        const existing = bookingMap[profKey] || [];
        const conflicts = [];
        for (const rb of rowBlocks) {
          for (const ex of existing) {
            if (isOverlap(rb.start, rb.end, ex.start, ex.end) && rb.day === ex.day) {
              const formatted = `${ex.day} ${formatDecimalToClock(ex.start)} - ${formatDecimalToClock(ex.end)}`;
              if (!conflicts.includes(formatted)) conflicts.push(formatted);
            }
          }
        }
        if (conflicts.length > 0) {
          label += ` (unavailable: ${conflicts.join(', ')})`;
        }
      }
      return { value: prof.name, profId: prof.id, label, disabled };
    });

    if (currentProfessor && currentProfessor !== 'TBA' && !assignedNames.has(currentProfessor)) {
      options.unshift({
        value: currentProfessor,
        profId: currentProfessorId,
        label: `${currentProfessor} (not assigned to subject)`,
        disabled: true,
      });
    }

    if (options.length === 0) {
      return [{ value: 'TBA', profId: '', label: 'TBA', disabled: true }];
    }

    return options;
  };

  const normalizeCourseId = (courseId) => (courseId || '').toString().trim().toLowerCase();
  const normalizeCourseName = (courseName) => (courseName || '').toString().trim().toLowerCase();
  const normalizeMatchKey = (value) => (value || '').toString().toLowerCase().replace(/[^a-z0-9]/g, '');

  const extractCourseEntry = (entry) => {
    const raw = (entry || '').toString().trim();
    const parts = raw.split(/\s*-\s*/);
    if (parts.length >= 2) {
      return {
        id: normalizeMatchKey(parts[0]),
        name: normalizeMatchKey(parts.slice(1).join(' - ')),
        raw: normalizeMatchKey(raw)
      };
    }
    return {
      id: normalizeMatchKey(raw),
      name: normalizeMatchKey(raw),
      raw: normalizeMatchKey(raw)
    };
  };

  const normalizeDepartmentName = (value) => (value || '').toString().trim().toLowerCase();
  const matchesDepartment = (value, dep) => {
    const normalizedDep = normalizeDepartmentName(dep);
    if (!normalizedDep) return false;
    if (Array.isArray(value)) {
      return value.some(item => normalizeDepartmentName(item) === normalizedDep);
    }
    return normalizeDepartmentName(value) === normalizedDep;
  };
  const professorHasDepartmentMatch = (profData, dep) => {
    if (!profData) return false;
    const matchesSelected = matchesDepartment(profData.department, dep) || matchesDepartment(profData.departments, dep);
    if (matchesSelected) return true;
    return isGeUser && (
      matchesDepartment(profData.department, 'General Education') ||
      matchesDepartment(profData.departments, 'General Education')
    );
  };

  const getMatchingProfessorCandidates = (course) => {
    const normalizedCourseId = normalizeMatchKey(course.courseId);
    const normalizedCourseName = normalizeMatchKey(course.courseName);
    return professors.filter(prof => {
      const profCourses = Array.isArray(prof.courses)
        ? prof.courses
        : typeof prof.courses === 'string'
          ? [prof.courses]
          : [];
      return profCourses.some(entry => {
        const normalized = extractCourseEntry(entry);
        return (
          (normalizedCourseId && (normalized.id === normalizedCourseId || normalized.raw.includes(normalizedCourseId))) ||
          (normalizedCourseName && (normalized.name === normalizedCourseName || normalized.raw.includes(normalizedCourseName)))
        );
      });
    });
  };

  const getRoomOperatingLimits = (room, day) => {
    const normalizedDay = normalizeDayName(day);
    if (room?.specificDayEntries && Array.isArray(room.specificDayEntries)) {
      const specific = room.specificDayEntries.find(entry => normalizeDayName(entry.date) === normalizedDay);
      if (specific) {
        const start = parseTimeToDecimal(specific.startTime);
        const end = parseTimeToDecimal(specific.endTime);
        if (!Number.isNaN(start) && !Number.isNaN(end) && end > start) {
          return { start, end };
        }
        return null;
      }
    }

    if (room?.roomStartTime || room?.roomEndTime) {
      const start = parseTimeToDecimal(room.roomStartTime);
      const end = parseTimeToDecimal(room.roomEndTime);
      if (!Number.isNaN(start) && !Number.isNaN(end) && end > start) {
        return { start, end };
      }
      return null;
    }

    return defaultDayLimits[day] || null;
  };

  const doesRoomSupportDay = (room, day) => {
    if (!room?.operatingDays || !Array.isArray(room.operatingDays) || room.operatingDays.length === 0) {
      return true;
    }
    const normalizedDay = normalizeDayName(day);
    return room.operatingDays.some(d => normalizeDayName(d) === normalizedDay);
  };

  const isOverlap = (start1, end1, start2, end2) => Math.max(start1, start2) < Math.min(end1, end2);

  const roomMatchesSubject = (room, course) => {
    const subjectIds = Array.isArray(room?.roomSubjectIds) ? room.roomSubjectIds.filter(Boolean) : [];
    const subjectNames = [
      ...(Array.isArray(room?.roomSubjectNames) ? room.roomSubjectNames : []),
      ...(room?.roomSubjectName ? [room.roomSubjectName] : [])
    ].filter(Boolean);
    if (subjectIds.length === 0 && subjectNames.length === 0) return true;

    const courseId = normalizeMatchKey(course.courseId);
    const courseName = normalizeMatchKey(course.courseName);
    return subjectIds.some(id =>
      normalizeMatchKey(id) === normalizeMatchKey(course.id) ||
      normalizeMatchKey(id) === courseId
    ) ||
      subjectNames.some(name => {
        const subject = extractCourseEntry(name);
        return (courseId && subject.id === courseId) || (courseName && subject.name === courseName);
      });
  };

  const roomMatchesDepartment = (room, dep) => {
    const assignedDepartments = [
      ...(Array.isArray(room?.departmentAssignedNames) ? room.departmentAssignedNames : []),
      ...(Array.isArray(room?.departments) ? room.departments : []),
      ...(room?.department ? [room.department] : [])
    ].filter(Boolean);
    if (assignedDepartments.length === 0) return true;
    return assignedDepartments.some(name => normalizeDepartmentName(name) === normalizeDepartmentName(dep));
  };

  const getAssignedDepartments = room => [
    ...(Array.isArray(room?.departmentAssignedNames) ? room.departmentAssignedNames : []),
    ...(Array.isArray(room?.departments) ? room.departments : []),
    ...(room?.department ? [room.department] : [])
  ].filter(Boolean);

  const getEligibleRooms = (type, course, dep, roomRecords = rooms) => {
    const requestedType = type === 'Lecture'
      ? course.lectureRoomType || type
      : course.labRoomType || type;
    const normalizedType = String(requestedType).trim().toLowerCase();
    return roomRecords.filter(room => {
      const roomType = (room.roomType || '').trim().toLowerCase();
      return roomType === normalizedType && roomMatchesSubject(room, course);
    }).sort((left, right) => {
      const priority = room => {
        const assignedDepartments = getAssignedDepartments(room);
        if (assignedDepartments.length === 0) return 1;
        return roomMatchesDepartment(room, dep) ? 0 : 2;
      };
      return priority(left) - priority(right);
    });
  };

  const getRoomAssignmentIssues = (generatedRows, roomRecords) => {
    const dep = normalizeDepartment(department, selectedDepartment);
    const issues = [];
    generatedRows.forEach(row => {
      const course = courses.find(item => item.courseId === row.courseId);
      if (!course) {
        issues.push(`${row.courseId}: curriculum course is no longer available.`);
        return;
      }
      const roomNames = splitScheduleSegments(row.room);
      const dayLabels = splitScheduleSegments(row.day);
      const times = splitScheduleSegments(row.time);
      const entryCount = Math.max(roomNames.length, dayLabels.length, times.length);
      for (let index = 0; index < entryCount; index += 1) {
        const roomName = roomNames[index] || roomNames[0] || '';
        const dayLabel = dayLabels[index] || dayLabels[0] || '';
        const timeLabel = times[index] || times[0] || '';
        const meetingType = /-\s*Lab(?:oratory)?\s*$/i.test(dayLabel) ? 'Laboratory' : 'Lecture';
        const day = dayLabel.split(' - ')[0]?.trim();
        const dayKey = normalizeDayName(day);
        const parsedTime = parseTimeRangeFromString(timeLabel);
        const eligibleRoom = getEligibleRooms(meetingType, course, dep, roomRecords)
          .find(room => normalizeRoomName(room.roomName) === normalizeRoomName(roomName));
        if (!eligibleRoom) {
          issues.push(`${row.courseId}: ${roomName || 'No room'} is no longer eligible for ${meetingType}.`);
          continue;
        }
        if (!dayKey || !parsedTime || !doesRoomSupportDay(eligibleRoom, day)) {
          issues.push(`${row.courseId}: ${roomName} is not available on ${day || 'the selected day'}.`);
          continue;
        }
        const limits = getRoomOperatingLimits(eligibleRoom, day);
        if (!limits || parsedTime.start < limits.start || parsedTime.end > limits.end) {
          issues.push(`${row.courseId}: ${roomName} is not available for ${timeLabel} on ${day}.`);
        }
      }
    });
    return issues;
  };

  const savedRoomBookings = useMemo(() => {
    const bookings = {};
    roomBookingSchedules
      .filter(row => row.schoolYear === selectedSchoolYear && row.semester === selectedSemester)
      .forEach(row => {
        const entries = getRoomScheduleEntries(row);
        entries.forEach(entry => {
          const { room, day, start, end } = entry;
          const roomKey = normalizeRoomName(room);
          const dayKey = normalizeDayName(day);
          if (!roomKey || !dayKey) return;
          bookings[roomKey] = bookings[roomKey] || {};
          bookings[roomKey][dayKey] = bookings[roomKey][dayKey] || [];
          bookings[roomKey][dayKey].push({ start, end });
        });
      });
    return bookings;
  }, [roomBookingSchedules, selectedSchoolYear, selectedSemester]);

  const getRoomBookingConflicts = (generatedRows, existingSchedules) => {
    const bookings = {};
    existingSchedules
      .filter(row => row.schoolYear === selectedSchoolYear && row.semester === selectedSemester)
      .forEach(row => {
        getRoomScheduleEntries(row).forEach(entry => {
          const roomKey = normalizeRoomName(entry.room);
          const dayKey = normalizeDayName(entry.day);
          if (!roomKey || !dayKey) return;
          bookings[roomKey] = bookings[roomKey] || {};
          bookings[roomKey][dayKey] = bookings[roomKey][dayKey] || [];
          bookings[roomKey][dayKey].push({ ...entry, courseId: row.courseId });
        });
      });

    const conflicts = [];
    generatedRows.forEach(row => {
      getRoomScheduleEntries(row).forEach(entry => {
        const roomKey = normalizeRoomName(entry.room);
        const dayKey = normalizeDayName(entry.day);
        if (!roomKey || !dayKey) return;
        const existing = bookings[roomKey]?.[dayKey] || [];
        const conflict = existing.find(slot => isOverlap(entry.start, entry.end, slot.start, slot.end));
        if (conflict) {
          conflicts.push(`${entry.room} on ${entry.day} ${formatDecimalToClock(entry.start)}-${formatDecimalToClock(entry.end)} overlaps ${conflict.courseId || 'an existing schedule'} (${formatDecimalToClock(conflict.start)}-${formatDecimalToClock(conflict.end)})`);
          return;
        }
        bookings[roomKey] = bookings[roomKey] || {};
        bookings[roomKey][dayKey] = [...existing, { ...entry, courseId: row.courseId }];
      });
    });
    return conflicts;
  };

  useEffect(() => {
    const dep = normalizeDepartment(department, selectedDepartment);
    if (!dep) return;
    const fetchYears = async () => {
      const q = query(collection(db, 'curriculums'), where('department', '==', dep));
      const snap = await getDocs(q);
      setCurriculumYears(snap.docs.map(doc => doc.data().year).filter(Boolean));
    };
    fetchYears();
  }, [department, selectedDepartment]);

  useEffect(() => {
    const dep = normalizeDepartment(department, selectedDepartment);
    if (!dep || !selectedCurriculumYear || !selectedSemester) {
      setAvailableYearLevels([]);
      setSelectedYearLevel('');
      return;
    }
    const fetchYearLevels = async () => {
      const q = query(
        collection(db, 'curriculum_subjects'),
        where('department', '==', dep),
        where('year', '==', selectedCurriculumYear),
        where('semester', '==', selectedSemester)
      );
      const snap = await getDocs(q);
      const levels = snap.docs.map(doc => doc.data().yearLevel).filter(Boolean);
      const sortedLevels = sortYearLevels(levels);
      setAvailableYearLevels(sortedLevels);
      if (!sortedLevels.includes(selectedYearLevel)) {
        setSelectedYearLevel('');
      }
    };
    fetchYearLevels();
  }, [department, selectedDepartment, selectedCurriculumYear, selectedSemester, selectedYearLevel]);

  useEffect(() => {
    const dep = normalizeDepartment(department, selectedDepartment);
    if (!dep || !selectedCurriculumYear || !selectedSemester || !selectedYearLevel) {
      setCourses([]);
      return;
    }
    const fetchCourses = async () => {
      const q = query(
        collection(db, 'curriculum_subjects'),
        where('department', '==', dep),
        where('year', '==', selectedCurriculumYear),
        where('semester', '==', selectedSemester),
        where('yearLevel', '==', selectedYearLevel)
      );
      const snap = await getDocs(q);
      const rows = snap.docs.map(doc => {
        const data = doc.data();
        return {
          id: doc.id,
          courseId: data.courseId || '',
          courseName: data.courseName || '',
          semester: data.semester || '',
          yearLevel: data.yearLevel || '',
          units: data.units || '',
          lectureHours: parseCourseDuration(data.lectureHours) || parseCourseDuration(data.lectureSessions?.[0]?.duration),
          labHours: parseCourseDuration(data.labHours) || parseCourseDuration(data.laboratorySessions?.[0]?.duration),
          lectureRoomType: data.lectureSessions?.[0]?.roomType || data.lecture?.roomType || 'Lecture',
          labRoomType: data.laboratorySessions?.[0]?.roomType || data.laboratory?.roomType || 'Laboratory',
          trainingHours: data.trainingHours || (Array.isArray(data.internshipSessions) && data.internshipSessions[0]?.duration ? parseFloat(data.internshipSessions[0].duration) : ''),
          isMinorSubject: Boolean(data.isMinorSubject),
        };
      });
      setCourses(rows);
      // keep existing preferredDays so GE auto-fill isn't immediately cleared
      setGeneratedSchedules([]);
    };
    fetchCourses();
  }, [department, selectedDepartment, selectedCurriculumYear, selectedSemester, selectedYearLevel, isGeUser]);

  useEffect(() => {
    const dep = normalizeDepartment(department, selectedDepartment);
    if (!dep) {
      setProfessors([]);
      return;
    }
    const fetchProfs = async () => {
      const q = query(collection(db, 'users'), where('role', '==', 'Professor'));
      const snap = await getDocs(q);
      const filtered = snap.docs
        .map(doc => ({ id: doc.id, ...doc.data() }))
        .filter(prof => professorHasDepartmentMatch(prof, dep));
      // include email so we can save `prof` (email) on schedule records for other components
      setProfessors(filtered.map(prof => ({
        id: prof.id,
        name: prof.name || 'TBA',
        email: prof.email || '',
        courses: prof.courses || [],
        schedules: prof.schedules || prof.schedule || [],
      })));
    };
    fetchProfs();
  }, [department, selectedDepartment, isGeUser]);

  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged((user) => {
      if (!user) {
        navigate('/academics');
      }
    });
    return unsubscribe;
  }, [navigate]);

  const fetchSavedSchedules = async () => {
    const dep = normalizeDepartment(department, selectedDepartment);
    if (!dep) {
      setSavedSchedules([]);
      return;
    }
    const q = query(collection(db, 'schedules'), where('department', '==', dep));
    const snap = await getDocs(q);
    setSavedSchedules(snap.docs.map(doc => ({ id: doc.id, ...doc.data() })));
  };

  useEffect(() => {
    const dep = normalizeDepartment(department, selectedDepartment);
    if (!dep) {
      setSavedSchedules([]);
      return;
    }

    const q = query(collection(db, 'schedules'), where('department', '==', dep));
    const unsubscribe = onSnapshot(q, (snapshot) => {
      setSavedSchedules(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
    }, (error) => {
      console.error('Failed to listen for schedule updates', error);
    });

    return unsubscribe;
  }, [department, selectedDepartment]);

  useEffect(() => {
    setRoomsLoading(true);
    setRoomsLoadError('');
    const unsubscribe = onSnapshot(collection(db, 'rooms'), snapshot => {
      setRooms(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
      setRoomsLoading(false);
      setRoomsLoadError('');
    }, error => {
      console.error('Failed to listen for room updates', error);
      setRooms([]);
      setRoomsLoading(false);
      setRoomsLoadError('Rooms could not be loaded. Check your access or connection, then reload Scheduling Management.');
    });
    return unsubscribe;
  }, []);

  useEffect(() => {
    if (!selectedSchoolYear) {
      setRoomBookingSchedules([]);
      setRoomBookingSchoolYear('');
      setRoomBookingsLoading(false);
      setRoomBookingsLoadError('');
      return;
    }

    setRoomBookingsLoading(true);
    setRoomBookingSchoolYear('');
    setRoomBookingsLoadError('');
    const termSchedulesQuery = query(
      collection(db, 'schedules'),
      where('schoolYear', '==', selectedSchoolYear)
    );
    const unsubscribe = onSnapshot(termSchedulesQuery, snapshot => {
      setRoomBookingSchedules(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
      setRoomBookingSchoolYear(selectedSchoolYear);
      setRoomBookingsLoading(false);
      setRoomBookingsLoadError('');
    }, error => {
      console.error('Failed to listen for room booking updates', error);
      setRoomBookingSchedules([]);
      setRoomBookingSchoolYear('');
      setRoomBookingsLoading(false);
      setRoomBookingsLoadError('Existing room bookings could not be loaded. Room availability cannot be verified.');
    });
    return unsubscribe;
  }, [selectedSchoolYear]);

  const savedYearLevels = useMemo(
    () => sortYearLevels(savedSchedules.map(item => item.yearLevel).filter(Boolean)),
    [savedSchedules]
  );
  const savedSemesters = useMemo(
    () => ['First Semester', 'Second Semester'].filter(sem => savedSchedules.some(item => item.semester === sem)),
    [savedSchedules]
  );
  const savedSections = useMemo(
    () => [...new Set(savedSchedules.map(item => item.section).filter(Boolean))].sort(),
    [savedSchedules]
  );
  const displaySchoolYears = useMemo(
    () => isGeUser ? [...new Set(savedSchedules.map(item => item.schoolYear).filter(Boolean))].sort() : schoolYears,
    [savedSchedules, isGeUser, schoolYears]
  );
  const displaySections = useMemo(
    () => isGeUser ? [...new Set(savedSchedules.map(item => item.section).filter(Boolean))].sort() : sections,
    [savedSchedules, isGeUser, sections]
  );
  const [selectedTodos, setSelectedTodos] = useState([]);
  const [departmentMinorCourses, setDepartmentMinorCourses] = useState([]);
  const todoItems = useMemo(() => {
    if (!isGeUser || departmentMinorCourses.length === 0) return [];
    const minorCourseIds = new Set(departmentMinorCourses.map(c => c.courseId).filter(Boolean));
    if (minorCourseIds.size === 0) return [];

    const groups = new Map();
    savedSchedules.forEach(row => {
      if (!row.schoolYear || !row.section || !row.semester || !row.yearLevel || !row.curriculumYear) return;
      const key = `${row.curriculumYear}||${row.schoolYear}||${row.section}||${row.semester}||${row.yearLevel}`;
      if (!groups.has(key)) {
        groups.set(key, {
          id: key,
          curriculumYear: row.curriculumYear,
          schoolYear: row.schoolYear,
          section: row.section,
          semester: row.semester,
          yearLevel: row.yearLevel,
          existing: new Set()
        });
      }
      groups.get(key).existing.add(row.courseId);
    });

    const items = [];
    groups.forEach(group => {
      const groupMinorCourseIds = new Set(departmentMinorCourses
        .filter(course =>
          course.curriculumYear === group.curriculumYear &&
          course.semester === group.semester &&
          course.yearLevel === group.yearLevel
        )
        .map(course => course.courseId)
        .filter(Boolean)
      );
      if (groupMinorCourseIds.size === 0) return;
      const missingCount = [...groupMinorCourseIds].filter(courseId => !group.existing.has(courseId)).length;
      if (missingCount > 0) {
        items.push({
          id: group.id,
          curriculumYear: group.curriculumYear,
          schoolYear: group.schoolYear,
          section: group.section,
          semester: group.semester,
          yearLevel: group.yearLevel,
          missingCount,
        });
      }
    });

    return items.sort((a, b) => {
      if (a.curriculumYear !== b.curriculumYear) return a.curriculumYear.localeCompare(b.curriculumYear);
      if (a.schoolYear !== b.schoolYear) return a.schoolYear.localeCompare(b.schoolYear);
      if (a.yearLevel !== b.yearLevel) return a.yearLevel.localeCompare(b.yearLevel);
      if (a.semester !== b.semester) return a.semester.localeCompare(b.semester);
      return a.section.localeCompare(b.section);
    });
  }, [isGeUser, departmentMinorCourses, savedSchedules]);

  const derivePreferredDaysFromSavedGroup = ({ schoolYear, section, semester, yearLevel, curriculumYear }) => {
    const majorScheduleRows = savedSchedules.filter(row =>
      row.schoolYear === schoolYear &&
      row.section === section &&
      row.semester === semester &&
      row.yearLevel === yearLevel &&
      row.curriculumYear === curriculumYear
    );

    const nextPreferred = {};
    majorScheduleRows.forEach(row => {
      const courseId = normalizeCourseId(row.courseId);
      if (!courseId) return;

      const rawDayText = row.day || '';
      const entries = [];
      const regex = /(Monday|Tuesday|Wednesday|Thursday|Friday)\s*-\s*(Lecture|Lec|Laboratory|Lab)/gi;
      let match;
      while ((match = regex.exec(rawDayText)) !== null) {
        entries.push({ day: match[1], typeText: match[2] });
      }

      if (entries.length === 0) {
        const lines = rawDayText
          .split(/\r?\n|,|;/)
          .map(line => line.trim())
          .filter(Boolean);
        lines.forEach(line => {
          const day = line.split('-')[0].trim();
          if (!day) return;
          const lower = line.toLowerCase();
          let typeText = null;
          if (lower.includes('laboratory') || lower.includes('lab')) typeText = 'Laboratory';
          else if (lower.includes('lecture') || lower.includes('lec')) typeText = 'Lecture';
          else if ((row.type || '').toLowerCase().includes('laboratory')) typeText = 'Laboratory';
          else if ((row.type || '').toLowerCase().includes('lecture')) typeText = 'Lecture';
          if (!typeText) return;
          entries.push({ day, typeText });
        });
      }

      entries.forEach(({ day, typeText }) => {
        const lower = typeText.toLowerCase();
        let keyType = null;
        if (lower.includes('laboratory') || lower.includes('lab')) keyType = 'Laboratory';
        else if (lower.includes('lecture') || lower.includes('lec')) keyType = 'Lecture';
        if (!keyType) return;

        const key = `${courseId}_${keyType}`;
        nextPreferred[key] = nextPreferred[key] || { days: [] };
        if (!nextPreferred[key].days.includes(day)) {
          nextPreferred[key].days.push(day);
        }
      });
    });

    setPreferredDays(prev => ({ ...prev, ...nextPreferred }));
  };

  const toggleTodo = (item) => {
    const id = item.id;
    const willSelect = !selectedTodos.includes(id);
    setSelectedTodos(willSelect ? [id] : []);
    setSelectedSchoolYear(willSelect ? item.schoolYear : '');
    setSelectedSection(willSelect ? item.section : '');
    setSelectedCurriculumYear(willSelect ? item.curriculumYear : '');
    setSelectedSemester(willSelect ? item.semester : '');
    setSelectedYearLevel(willSelect ? item.yearLevel : '');
    if (willSelect) {
      derivePreferredDaysFromSavedGroup(item);
    }
  };

  const selectTodoItem = (item) => {
    const id = item.id;
    setSelectedTodos([id]);
    setSelectedSchoolYear(item.schoolYear);
    setSelectedSection(item.section);
    setSelectedCurriculumYear(item.curriculumYear);
    setSelectedSemester(item.semester);
    setSelectedYearLevel(item.yearLevel);
    derivePreferredDaysFromSavedGroup(item);
  };
  const filteredSavedSchedules = useMemo(
    () => savedSchedules.filter(row =>
      (!savedFilterYearLevel || row.yearLevel === savedFilterYearLevel) &&
      (!savedFilterSemester || row.semester === savedFilterSemester) &&
      (!savedFilterSection || row.section === savedFilterSection)
    ),
    [savedSchedules, savedFilterYearLevel, savedFilterSemester, savedFilterSection]
  );

  useEffect(() => {
    setSelectedSchoolYear('');
    setSelectedSection('');
    if (isGeUser) {
      setNewSchoolYear('');
      setNewSection('');
    }
  }, [selectedDepartment, department, isGeUser]);

  useEffect(() => {
    const dep = normalizeDepartment(department, selectedDepartment);
    if (!isGeUser || !dep) {
      setDepartmentMinorCourses([]);
      return;
    }

    const fetchMinorCourses = async () => {
      const q = query(collection(db, 'curriculum_subjects'), where('department', '==', dep));
      const snap = await getDocs(q);
      const rows = snap.docs
        .map(doc => doc.data())
        .filter(data => Boolean(data.isMinorSubject))
        .map(data => ({
          courseId: data.courseId || '',
          courseName: data.courseName || '',
          curriculumYear: data.year || '',
          semester: data.semester || '',
          yearLevel: data.yearLevel || ''
        }));
      setDepartmentMinorCourses(rows);
    };

    fetchMinorCourses();
  }, [department, selectedDepartment, isGeUser]);

  useEffect(() => {
    if (!isGeUser) return;
    const dep = normalizeDepartment(department, selectedDepartment);
    if (!dep || !selectedSchoolYear || !selectedSection || !selectedCurriculumYear || !selectedSemester || !selectedYearLevel) {
      return;
    }

    const majorScheduleRows = savedSchedules.filter(row =>
      row.schoolYear === selectedSchoolYear &&
      row.section === selectedSection &&
      row.semester === selectedSemester &&
      row.yearLevel === selectedYearLevel &&
      row.curriculumYear === selectedCurriculumYear
    );

    const nextPreferred = {};
    majorScheduleRows.forEach(row => {
      const courseId = normalizeCourseId(row.courseId);
      if (!courseId) return;

      const rawDayText = row.day || '';
      const entries = [];
      const regex = /(Monday|Tuesday|Wednesday|Thursday|Friday)\s*-\s*(Lecture|Lec|Laboratory|Lab)/gi;
      let match;
      while ((match = regex.exec(rawDayText)) !== null) {
        entries.push({ day: match[1], typeText: match[2] });
      }

      if (entries.length === 0) {
        const lines = rawDayText
          .split(/\r?\n|,|;/)
          .map(line => line.trim())
          .filter(Boolean);
        lines.forEach(line => {
          const day = line.split('-')[0].trim();
          if (!day) return;
          const lower = line.toLowerCase();
          let typeText = null;
          if (lower.includes('laboratory') || lower.includes('lab')) typeText = 'Laboratory';
          else if (lower.includes('lecture') || lower.includes('lec')) typeText = 'Lecture';
          else if ((row.type || '').toLowerCase().includes('laboratory')) typeText = 'Laboratory';
          else if ((row.type || '').toLowerCase().includes('lecture')) typeText = 'Lecture';
          if (!typeText) return;
          entries.push({ day, typeText });
        });
      }

      entries.forEach(({ day, typeText }) => {
        const lower = typeText.toLowerCase();
        let keyType = null;
        if (lower.includes('laboratory') || lower.includes('lab')) keyType = 'Laboratory';
        else if (lower.includes('lecture') || lower.includes('lec')) keyType = 'Lecture';
        if (!keyType) return;

        const key = `${courseId}_${keyType}`;
        nextPreferred[key] = nextPreferred[key] || { days: [] };
        if (!nextPreferred[key].days.includes(day)) {
          nextPreferred[key].days.push(day);
        }
      });
    });

    setPreferredDays(prev => ({ ...prev, ...nextPreferred }));
  }, [isGeUser, department, selectedDepartment, selectedSchoolYear, selectedSection, selectedCurriculumYear, selectedSemester, selectedYearLevel, savedSchedules]);

  useEffect(() => {
    try {
      const raw = localStorage.getItem('lunchBreakMap');
      if (raw) {
        setLunchBreakMap(JSON.parse(raw));
      }
    } catch (err) {
      // ignore localStorage errors
    }
  }, []);

  const getNextBreakForKey = key => {
    let next = '11-12';
    setLunchBreakMap(prev => {
      const options = ['11-12', '12-1', '1-2'];
      const current = prev[key];
      const nextIdx = (options.indexOf(current) + 1) % options.length;
      next = options[nextIdx];
      const nextMap = { ...prev, [key]: next };
      try { localStorage.setItem('lunchBreakMap', JSON.stringify(nextMap)); } catch (err) {}
      return nextMap;
    });
    return next;
  };

  const togglePreferredDay = (courseId, type, day) => {
    const key = `${normalizeCourseId(courseId)}_${type}`;
    setPreferredDays(prev => {
      const currentDays = prev[key]?.days || [];
      const nextDays = currentDays.includes(day)
        ? currentDays.filter(d => d !== day)
        : [...currentDays, day];
      return {
        ...prev,
        [key]: { days: nextDays }
      };
    });
  };

  const hasLunchGap = (candidate, end) => {
    if (end <= 10 || candidate >= 13) return true;
    const overlapStart = Math.max(candidate, 10);
    const overlapEnd = Math.min(end, 13);
    const freeBefore = Math.max(0, candidate - 10);
    const freeAfter = Math.max(0, 13 - end);
    return freeBefore + freeAfter >= 1;
  };

  const findAvailableDaysForCourse = (course, typeLabel, duration) => {
    const dep = normalizeDepartment(department, selectedDepartment);
    const requiredType = typeLabel === 'Lecture' ? 'Lecture' : 'Laboratory';
    const courseEligibleRooms = getEligibleRooms(requiredType, course, dep);
    const availableDays = [];
    const blockSize = Math.min(1, duration || 1);

    dayOptions.forEach(day => {
      const eligibleRooms = courseEligibleRooms.filter(room => doesRoomSupportDay(room, day));
      if (eligibleRooms.length === 0) return;

      for (const room of eligibleRooms) {
        const limits = getRoomOperatingLimits(room, day);
        if (!limits) continue;

        const savedRoomDay = (savedRoomBookings[normalizeRoomName(room.roomName)]?.[normalizeDayName(day)] || []);
        let candidate = limits.start;

        while (candidate + blockSize <= limits.end) {
          const end = candidate + blockSize;
          if (!hasLunchGap(candidate, end)) {
            candidate += 0.25;
            continue;
          }
          const conflict = savedRoomDay.some(slot => isOverlap(candidate, end, slot.start, slot.end));
          if (!conflict) {
            availableDays.push(day);
            return;
          }
          candidate += 0.25;
        }
      }
    });

    return [...new Set(availableDays)];
  };

  const handleGeneratePreferredDays = () => {
    if (roomsLoading || roomsLoadError || roomBookingsLoading || roomBookingsLoadError || roomBookingSchoolYear !== selectedSchoolYear) {
      setStatusIsError(true);
      setStatusMessage(roomsLoadError || roomBookingsLoadError || 'Wait for room and booking data to finish loading before checking preferred days.');
      return;
    }
    const generationCourses = isGeUser ? courses.filter(course => course.isMinorSubject) : courses;
    if (generationCourses.length === 0) {
      setStatusMessage(isGeUser ? 'No minor subjects available to generate preferred days.' : 'No curriculum subjects available to generate preferred days.');
      return;
    }

    const nextPreferred = {};
    generationCourses.forEach(course => {
      const courseIdKey = normalizeCourseId(course.courseId);
      if (!courseIdKey) return;
      if (course.lectureHours && parseFloat(course.lectureHours) > 0) {
        const lectureDays = findAvailableDaysForCourse(course, 'Lecture', parseFloat(course.lectureHours));
        nextPreferred[`${courseIdKey}_Lecture`] = { days: lectureDays };
      }
      if (course.labHours && parseFloat(course.labHours) > 0) {
        const labDays = findAvailableDaysForCourse(course, 'Laboratory', parseFloat(course.labHours));
        nextPreferred[`${courseIdKey}_Laboratory`] = { days: labDays };
      }
    });

    setPreferredDays(prev => ({ ...prev, ...nextPreferred }));
    setStatusIsError(false);
    setStatusMessage('Preferred Days generated from available room slots. Review the selections and regenerate schedule.');
  };

  const handleGenerateSchedule = () => {
    if (!selectedSchoolYear || !selectedSection) {
      setStatusMessage('Select school year and section before generating schedule.');
      return;
    }
    if (!selectedCurriculumYear || !selectedSemester || !selectedYearLevel) {
      setStatusMessage('Select curriculum year, semester, and year level first.');
      return;
    }
    if (roomsLoadError || roomsLoading) {
      setStatusIsError(true);
      setStatusMessage(roomsLoadError || 'Room data is still loading. Wait before generating schedules.');
      return;
    }
    if (roomBookingsLoadError || roomBookingsLoading || roomBookingSchoolYear !== selectedSchoolYear) {
      setStatusIsError(true);
      setStatusMessage(roomBookingsLoadError || 'Existing room bookings are still loading. Wait before generating schedules.');
      return;
    }
    const generationCourses = isGeUser ? courses.filter(course => course.isMinorSubject) : courses;
    if (generationCourses.length === 0) {
      setStatusMessage(isGeUser ? 'No minor subjects available for the selected filters.' : 'No curriculum subjects available for the selected filters.');
      return;
    }

    const dep = normalizeDepartment(department, selectedDepartment);
    if (!isGeUser) {
      const existingForSection = savedSchedules.some(s =>
        s.section === selectedSection &&
        s.schoolYear === selectedSchoolYear &&
        s.yearLevel === selectedYearLevel &&
        s.semester === selectedSemester
      );
      if (existingForSection) {
        setStatusMessage(`Section ${selectedSection} already has saved schedules for ${selectedSchoolYear}, ${selectedSemester}, ${selectedYearLevel}. Generation aborted.`);
        setStatusIsError(true);
        return;
      }
    }

    // For GE users we consider duplicates at the department + schoolYear + section level
    // (ignore yearLevel/semester) so a minor subject already scheduled in that group
    // cannot be generated again. For non-GE users keep the original stricter check.
    const existingCourseIds = new Set(savedSchedules
      .filter(s =>
        s.section === selectedSection &&
        s.schoolYear === selectedSchoolYear &&
        (isGeUser ? true : (s.yearLevel === selectedYearLevel && s.semester === selectedSemester))
      )
      .map(s => s.courseId)
    );
    const alreadyScheduled = generationCourses.filter(course => existingCourseIds.has(course.courseId));
    if (alreadyScheduled.length > 0) {
      setStatusMessage(`The following courses already have saved schedules in this group: ${alreadyScheduled.map(c => c.courseId).join(', ')}. Remove existing schedule records or choose another section.`);
      setStatusIsError(true);
      return;
    }
    const breakKey = `${dep}__${selectedSchoolYear}__${selectedSection}`;
    const assignedBreak = getNextBreakForKey(breakKey);
    setLastAssignedBreak(assignedBreak);

    const professorBookings = {};
    const addProfessorBooking = (name, day, start, end) => {
      if (!name || !day || Number.isNaN(start) || Number.isNaN(end)) return;
      professorBookings[name] = professorBookings[name] || {};
      professorBookings[name][day] = [...(professorBookings[name][day] || []), { start, end }];
    };
    const isProfessorFree = (name, day, start, end) => {
      const existing = professorBookings[name]?.[day] || [];
      return !existing.some(slot => isOverlap(start, end, slot.start, slot.end));
    };

    savedSchedules.forEach(row => {
      const professorName = row.professor;
      if (!professorName || professorName.toUpperCase() === 'TBA' || professorName.toUpperCase() === 'UNASSIGNED') return;
      if (!row.day || !row.time) return;
      const dayLabels = row.day.split('\n').map(d => d.trim()).filter(Boolean);
      const timeLabels = row.time.split('\n').map(t => t.trim()).filter(Boolean);
      for (let i = 0; i < dayLabels.length; i++) {
        const dayLabel = dayLabels[i];
        const day = normalizeDayName(dayLabel.split(' - ')[0]?.trim());
        const timeLabel = timeLabels[i] || timeLabels[0];
        const parsed = parseTimeRangeFromString(timeLabel);
        if (!day || !parsed) continue;
        addProfessorBooking(professorName, day, parsed.start, parsed.end);
      }
    });

    // Also include schedules stored on professor user documents to avoid conflicts
    professors.forEach(prof => {
      const profName = prof.name;
      const profSchedules = Array.isArray(prof.schedules) ? prof.schedules : [];
      profSchedules.forEach(s => {
        // Only consider entries for the same term when term filters are selected
        if (selectedSchoolYear && s.schoolYear && s.schoolYear !== selectedSchoolYear) return;
        if (selectedSemester && s.semester && s.semester !== selectedSemester) return;
        if (!s.day || !s.time) return;
        const blocks = getRowTimeBlocks(s);
        blocks.forEach(b => addProfessorBooking(profName, b.day, b.start, b.end));
      });
    });

    const assignProfessorToCourse = (course, blocks) => {
      const candidates = getMatchingProfessorCandidates(course);
      if (candidates.length === 0) return null;
      for (const prof of candidates) {
          const professorName = prof.name;
          const professorId = prof.id;
          const professorEmail = prof.email || '';
          const available = blocks.every(block => {
            // If the block is UNSCHEDULED we cannot check a time conflict here —
            // treat it as available for assignment so generation can still assign a professor.
            if (!block.time || block.time === 'UNSCHEDULED') return true;
            const parsed = parseTimeRangeFromString(block.time);
            return parsed && isProfessorFree(professorName, block.day, parsed.start, parsed.end);
          });
        if (!available) continue;
        blocks.forEach(block => {
          const parsed = parseTimeRangeFromString(block.time);
          if (!block.time || block.time === 'UNSCHEDULED' || !parsed) return;
          addProfessorBooking(professorName, block.day, parsed.start, parsed.end);
        });
          return { name: professorName, id: professorId, email: professorEmail };
      }
      return null;
    };

    const noProfessorAssignedCourseIds = new Set();

    const breakWindows = [];
    switch (assignedBreak) {
      case '11-12': breakWindows.push({ start: 11, end: 12 }); break;
      case '12-1': breakWindows.push({ start: 12, end: 13 }); break;
      case '1-2': breakWindows.push({ start: 13, end: 14 }); break;
      default: break;
    }

    const formatTime = hour => {
      const hourInt = Math.floor(hour);
      const minute = Math.round((hour - hourInt) * 60);
      const period = hourInt >= 12 ? 'PM' : 'AM';
      let displayHour = hourInt % 12;
      if (displayHour === 0) displayHour = 12;
      return `${String(displayHour).padStart(2, '0')}:${String(minute).padStart(2, '0')} ${period}`;
    };

    const sectionSchedules = {};
    const roomSchedules = {};
    // collect existing saved bookings for the target section/group to avoid conflicts
    const savedSectionBookings = {};
    savedSchedules
      .filter(s =>
        s.schoolYear === selectedSchoolYear &&
        s.section === selectedSection &&
        s.curriculumYear === selectedCurriculumYear &&
        s.semester === selectedSemester &&
        s.yearLevel === selectedYearLevel
      )
      .forEach(row => {
        if (!row.day || !row.time) return;
        const dayLabels = row.day.split('\n').map(d => d.trim()).filter(Boolean);
        const timeLabels = row.time.split('\n').map(t => t.trim()).filter(Boolean);
        for (let i = 0; i < dayLabels.length; i++) {
          const dayLabel = dayLabels[i];
          const day = dayLabel.split(' - ')[0]?.trim();
          const timeLabel = timeLabels[i] || timeLabels[0];
          if (!day || !timeLabel) continue;
          const match = /^([0-9]{1,2}:[0-9]{2}\s*(?:AM|PM))\s*-\s*([0-9]{1,2}:[0-9]{2}\s*(?:AM|PM))$/i.exec(timeLabel);
          if (!match) continue;
          const start = parseTime12HourToDecimal(match[1]);
          const end = parseTime12HourToDecimal(match[2]);
          if (Number.isNaN(start) || Number.isNaN(end)) continue;
          savedSectionBookings[day] = savedSectionBookings[day] || [];
          savedSectionBookings[day].push({ start, end });
        }
      });
    const rows = [];
    let unscheduledCount = 0;
    const unavailableCourseIds = new Set();
    const alternativeDaySuggestions = {};
    const courseIssueReasons = {};
    const addCourseIssueReason = (courseId, reason) => {
      if (!courseId || !reason) return;
      courseIssueReasons[courseId] = courseIssueReasons[courseId] || [];
      if (!courseIssueReasons[courseId].includes(reason)) {
        courseIssueReasons[courseId].push(reason);
      }
    };

    const findAvailableDaysForCourseBlock = (course, typeLabel, duration, failedDay) => {
      const requiredType = typeLabel === 'Lecture' ? 'Lecture' : 'Laboratory';
      const availableDays = [];
      const courseEligibleRooms = getEligibleRooms(requiredType, course, dep);

      dayOptions.forEach(day => {
        if (day === failedDay) return;
        const eligibleRooms = courseEligibleRooms.filter(room => doesRoomSupportDay(room, day));
        if (eligibleRooms.length === 0) return;

        const dayKey = normalizeDayName(day);
        const sectionDayScheduled = sectionSchedules[dayKey] || [];
        for (const room of eligibleRooms) {
          const limits = getRoomOperatingLimits(room, day);
          if (!limits) continue;

          const savedRoomDay = (savedRoomBookings[normalizeRoomName(room.roomName)]?.[normalizeDayName(day)] || []);
          const roomKey = normalizeRoomName(room.roomName);
          const generatedRoomDay = (roomSchedules[roomKey]?.[dayKey] || []);
          let candidate = limits.start;

          while (candidate + duration <= limits.end) {
            const end = candidate + duration;
            if (breakWindows.some(bw => isOverlap(candidate, end, bw.start, bw.end))) {
              candidate += 0.25;
              continue;
            }
            const conflict = [
              ...savedRoomDay,
              ...generatedRoomDay,
              ...sectionDayScheduled,
              ...(savedSectionBookings[dayKey] || [])
            ].some(slot => isOverlap(candidate, end, slot.start, slot.end));
            if (!conflict) {
              availableDays.push(day);
              return;
            }
            candidate += 0.25;
          }
        }
      });

      return [...new Set(availableDays)];
    };

    const assignBlockToRoom = (course, typeLabel, day, duration) => {
      if (duration <= 0) return { startTime: null, roomName: null };
      const requiredType = typeLabel === 'Lecture' ? 'Lecture' : 'Laboratory';
      const eligibleRooms = getEligibleRooms(requiredType, course, dep).filter(room => doesRoomSupportDay(room, day));
      if (eligibleRooms.length === 0) return { startTime: null, roomName: null, triedRooms: [] };

      const triedRooms = [];

      const dayKey = normalizeDayName(day);
      const sectionDayScheduled = sectionSchedules[dayKey] || [];
      for (const room of eligibleRooms) {
        const limits = getRoomOperatingLimits(room, day);
        if (!limits) continue;

        const savedRoomDay = (savedRoomBookings[normalizeRoomName(room.roomName)]?.[normalizeDayName(day)] || []);
        const roomKey = normalizeRoomName(room.roomName);
        const generatedRoomDay = (roomSchedules[roomKey]?.[dayKey] || []);
        let candidate = limits.start;

        while (candidate + duration <= limits.end) {
          const end = candidate + duration;
          if (breakWindows.some(bw => isOverlap(candidate, end, bw.start, bw.end))) {
            candidate += 0.25;
            continue;
          }
          const conflict = [
            ...savedRoomDay,
            ...generatedRoomDay,
            ...sectionDayScheduled,
            ...(savedSectionBookings[dayKey] || [])
          ].some(slot => isOverlap(candidate, end, slot.start, slot.end));
          if (!conflict) {
            return { startTime: candidate, roomName: room.roomName, triedRooms };
          }
          candidate += 0.25;
        }
        // could not fit in this room
        triedRooms.push(room.roomName);
      }
      return { startTime: null, roomName: null, triedRooms };
    };

    generationCourses.forEach(course => {
      const courseIdKey = normalizeCourseId(course.courseId);
      const lectureKey = `${courseIdKey}_Lecture`;
      const labKey = `${courseIdKey}_Laboratory`;
      const lectureDays = preferredDays[lectureKey]?.days || [];
      const labDays = preferredDays[labKey]?.days || [];
      const lectureDuration = parseFloat(course.lectureHours) || 0;
      const labDuration = parseFloat(course.labHours) || 0;
      const lectureDurationPerDay = lectureDays.length > 0 ? lectureDuration / lectureDays.length : lectureDuration;
      const labDurationPerDay = labDays.length > 0 ? labDuration / labDays.length : labDuration;
      const blocks = [];

      lectureDays.forEach(day => {
        const duration = lectureDurationPerDay;
        const { startTime, roomName, triedRooms } = assignBlockToRoom(course, 'Lecture', day, duration) || {};
        const dayOrder = dayOptions.indexOf(day);
        if (startTime === null) {
          blocks.push({ label: `${day} - Lec`, day, time: 'UNSCHEDULED', room: 'UNASSIGNED', sortType: 0, dayOrder });
          unscheduledCount += 1;
          unavailableCourseIds.add(course.courseId);
          const roomReason = triedRooms && triedRooms.length > 0
            ? `No available room in ${triedRooms.join(', ')}`
            : 'No eligible rooms available for this day and subject';
          addCourseIssueReason(course.courseId, `No available room for Lecture on ${day} (${roomReason})`);
          const availableDays = findAvailableDaysForCourseBlock(course, 'Lecture', duration, day);
          if (availableDays.length > 0) {
            alternativeDaySuggestions[course.courseId] = availableDays;
          }
          return;
        }
        const endTime = startTime + duration;
        const dayKey = normalizeDayName(day);
        const roomKey = normalizeRoomName(roomName);
        sectionSchedules[dayKey] = [...(sectionSchedules[dayKey] || []), { start: startTime, end: endTime }].sort((a, b) => a.start - b.start);
        roomSchedules[roomKey] = {
          ...roomSchedules[roomKey],
          [dayKey]: [...(roomSchedules[roomKey]?.[dayKey] || []), { start: startTime, end: endTime }].sort((a, b) => a.start - b.start)
        };
        blocks.push({ label: `${day} - Lec`, day, time: `${formatTime(startTime)} - ${formatTime(endTime)}`, room: roomName, sortType: 0, dayOrder });
      });

      labDays.forEach(day => {
        const duration = labDurationPerDay;
        const { startTime, roomName, triedRooms } = assignBlockToRoom(course, 'Laboratory', day, duration) || {};
        const dayOrder = dayOptions.indexOf(day);
        if (startTime === null) {
          blocks.push({ label: `${day} - Lab`, day, time: 'UNSCHEDULED', room: 'UNASSIGNED', sortType: 1, dayOrder });
          unscheduledCount += 1;
          unavailableCourseIds.add(course.courseId);
          const roomReason = triedRooms && triedRooms.length > 0
            ? `No available room in ${triedRooms.join(', ')}`
            : 'No eligible rooms available for this day and subject';
          addCourseIssueReason(course.courseId, `No available room for Laboratory on ${day} (${roomReason})`);
          const availableDays = findAvailableDaysForCourseBlock(course, 'Laboratory', duration, day);
          if (availableDays.length > 0) {
            alternativeDaySuggestions[course.courseId] = availableDays;
          }
          return;
        }
        const endTime = startTime + duration;
        const dayKey = normalizeDayName(day);
        const roomKey = normalizeRoomName(roomName);
        sectionSchedules[dayKey] = [...(sectionSchedules[dayKey] || []), { start: startTime, end: endTime }].sort((a, b) => a.start - b.start);
        roomSchedules[roomKey] = {
          ...roomSchedules[roomKey],
          [dayKey]: [...(roomSchedules[roomKey]?.[dayKey] || []), { start: startTime, end: endTime }].sort((a, b) => a.start - b.start)
        };
        blocks.push({ label: `${day} - Lab`, day, time: `${formatTime(startTime)} - ${formatTime(endTime)}`, room: roomName, sortType: 1, dayOrder });
      });

      if (blocks.length > 0) {
        blocks.sort((a, b) => a.dayOrder - b.dayOrder || a.sortType - b.sortType);
        const profResult = assignProfessorToCourse(course, blocks);
        const professorName = profResult ? profResult.name : 'TBA';
        const professorId = profResult ? profResult.id : '';
        if (professorName === 'TBA') {
          noProfessorAssignedCourseIds.add(course.courseId);
          addCourseIssueReason(course.courseId, 'No available professor found for scheduled blocks.');
        }
        rows.push({
          id: `${course.courseId}_Schedule`,
          yearLevel: course.yearLevel,
          semester: course.semester,
          section: selectedSection,
          courseId: course.courseId,
          courseName: course.courseName,
          day: blocks.map(block => block.label).join('\n'),
          time: blocks.map(block => block.time).join('\n'),
          units: course.units || '',
          room: blocks.map(block => block.room).join('\n'),
          professor: professorName,
          professorId: professorId,
          professorEmail: profResult?.email || '',
          type: course.lectureHours && course.labHours ? 'Lecture / Laboratory' : course.lectureHours ? 'Lecture' : 'Laboratory'
        });
      }

      if (course.trainingHours && !course.trainingHours.toString().startsWith('0')) {
        const trainingProfResult = assignProfessorToCourse(course, []);
        const trainingProfessor = trainingProfResult ? trainingProfResult.name : 'TBA';
        const trainingProfId = trainingProfResult ? trainingProfResult.id : '';
        if (trainingProfessor === 'TBA') {
          noProfessorAssignedCourseIds.add(course.courseId);
          addCourseIssueReason(course.courseId, 'No available training professor found.');
        }
        rows.push({
          id: `${course.courseId}_Training`,
          yearLevel: course.yearLevel,
          semester: course.semester,
          section: selectedSection,
          courseId: course.courseId,
          courseName: course.courseName,
          day: 'TBD',
          time: `${course.trainingHours} hrs`,
          units: course.units || '',
          room: 'TBA',
          professor: trainingProfessor,
          professorId: trainingProfId,
          type: 'Training'
        });
      }
    });

    if (rows.length === 0) {
      setStatusMessage('No schedule rows created. Ensure preferred days are set for lecture or lab hours.');
      setGeneratedSchedules([]);
      return;
    }

    const message = `Generated ${rows.length} schedule rows using lunch break ${assignedBreak.replace('-', ' - ')}.`;
    let summary = message;
    if (unscheduledCount > 0) {
      summary += ' Could not be scheduled.';
      const courseIds = [...unavailableCourseIds];
      if (courseIds.length > 0) {
        summary += ` Details: ${courseIds.join(', ')}`;
      }
      const suggestionEntries = Object.entries(alternativeDaySuggestions).map(
        ([courseId, days]) => `${courseId} [${days.join(', ')}]`
      );
      if (suggestionEntries.length > 0) {
        summary += ` Suggested days: ${suggestionEntries.join(' ; ')}`;
      }
    }
    const issueEntries = Object.entries(courseIssueReasons).map(
      ([courseId, reasons]) => `${courseId}: ${reasons.join('; ')}`
    );
    if (issueEntries.length > 0) {
      summary += ` Issue details: ${issueEntries.join(' ; ')}`;
    }
    if (unscheduledCount > 0 || noProfessorAssignedCourseIds.size > 0) {
      setStatusIsError(true);
    } else {
      setStatusIsError(false);
    }
    setGeneratedSchedules(rows);
    setStatusMessage(summary);
  };

  const handleRemoveGeneratedRow = () => {
    if (!generatedRowToRemove) return;
    setGeneratedSchedules(prev => prev.filter(row => row.id !== generatedRowToRemove.id));
    setGeneratedRowToRemove(null);
  };

  const handleProfessorChange = (id, professor) => {
    // When professor name changes, look up the correct professorId
    const selectedProf = professors.find(p => p.name === professor);
    const professorId = selectedProf ? selectedProf.id : '';
    const professorEmail = selectedProf ? (selectedProf.email || '') : '';
    setGeneratedSchedules(prev => prev.map(row => row.id === id ? { ...row, professor, professorId, professorEmail } : row));
  };

  const hasUnscheduledBlocks = useMemo(() => {
    return generatedSchedules.some(row => row.time.includes('UNSCHEDULED') || row.day.includes('UNSCHEDULED'));
  }, [generatedSchedules]);

  const handleDeleteSavedSchedule = async () => {
    if (!scheduleToDelete) return;
    try {
      const scheduleRef = doc(db, 'schedules', scheduleToDelete.id);
      await runTransaction(db, async transaction => {
        const scheduleSnapshot = await transaction.get(scheduleRef);
        if (!scheduleSnapshot.exists()) return;

        const schedule = { id: scheduleSnapshot.id, ...scheduleSnapshot.data() };
        const lockRefs = new Map();
        if (schedule.schoolYear && schedule.semester) {
          getRoomScheduleEntries(schedule).forEach(entry => {
            const lockId = getRoomBookingLockId(schedule.schoolYear, schedule.semester, entry.room, entry.day);
            lockRefs.set(lockId, doc(db, 'roomAvailabilityLocks', lockId));
          });
        }

        const lockSnapshots = await Promise.all(
          [...lockRefs.entries()].map(async ([lockId, lockRef]) => [lockId, lockRef, await transaction.get(lockRef)])
        );
        lockSnapshots.forEach(([, lockRef, lockSnapshot]) => {
          if (!lockSnapshot.exists()) return;
          const bookings = Array.isArray(lockSnapshot.data().bookings) ? lockSnapshot.data().bookings : [];
          const remainingBookings = bookings.filter(booking => booking.scheduleId !== schedule.id);
          if (remainingBookings.length === bookings.length) return;
          if (remainingBookings.length === 0) {
            transaction.delete(lockRef);
          } else {
            transaction.update(lockRef, { bookings: remainingBookings });
          }
        });
        transaction.delete(scheduleRef);
      });
      setSavedSchedules(prev => prev.filter(item => item.id !== scheduleToDelete.id));
      setStatusIsError(false);
      setStatusMessage('Saved schedule removed successfully.');
      setScheduleToDelete(null);
    } catch (err) {
      console.error(err);
      setStatusIsError(true);
      setStatusMessage('Failed to remove saved schedule: ' + (err.message || 'Unknown error'));
    }
  };

  const handleSaveGenerated = async () => {
    if (generatedSchedules.length === 0) {
      setStatusMessage('No generated schedule rows to save.');
      return;
    }
    if (hasUnscheduledBlocks) {
      setStatusMessage('Cannot save schedule: some schedule blocks are unscheduled. Please resolve all UNSCHEDULED items before saving.');
      return;
    }
    if (roomsLoading || roomsLoadError) {
      setStatusIsError(true);
      setStatusMessage(roomsLoadError || 'Room data is still loading. Wait before saving the schedule.');
      return;
    }
    const dep = normalizeDepartment(department, selectedDepartment);
    if (!dep) {
      setStatusMessage('Department is not selected.');
      return;
    }
    if (!selectedSchoolYear || !selectedSemester) {
      setStatusIsError(true);
      setStatusMessage('Select a school year and semester before saving the schedule.');
      return;
    }
    if (roomBookingsLoading || roomBookingsLoadError || roomBookingSchoolYear !== selectedSchoolYear) {
      setStatusIsError(true);
      setStatusMessage(roomBookingsLoadError || 'Existing room bookings are still loading. Wait before saving the schedule.');
      return;
    }
    // Validate professor availability against saved schedules and professor records
    // Key by professorId to match accurately, track conflict source for debugging
    const validationBooking = {}; // tracks by profId: { profId -> [{day, start, end, source}] }
    const addToValidationBooking = (profId, day, start, end, source) => {
      if (!profId || !day || Number.isNaN(start) || Number.isNaN(end)) return;
      validationBooking[profId] = validationBooking[profId] || [];
      validationBooking[profId].push({ day, start, end, source });
    };
    // existing saved schedules
    savedSchedules.forEach(row => {
      const key = row.professorId || row.professor;
      if (!key || key.toUpperCase?.() === 'TBA' || key.toUpperCase?.() === 'UNASSIGNED') return;
      const blocks = getRowTimeBlocks(row);
      blocks.forEach(b => addToValidationBooking(key, b.day, b.start, b.end, 'saved-schedule'));
    });
    // professor user docs schedules
    professors.forEach(prof => {
      const profId = prof.id;
      const profSchedules = Array.isArray(prof.schedules) ? prof.schedules : [];
      profSchedules.forEach(s => {
        if (selectedSchoolYear && s.schoolYear && s.schoolYear !== selectedSchoolYear) return;
        if (selectedSemester && s.semester && s.semester !== selectedSemester) return;
        const blocks = getRowTimeBlocks(s);
        blocks.forEach(b => addToValidationBooking(profId, b.day, b.start, b.end, 'prof-doc-schedule'));
      });
    });

    const conflicts = [];
    // Validate generated rows sequentially, adding them into booking so later rows consider earlier ones
    for (const row of generatedSchedules) {
      const key = row.professorId || row.professor;
      if (!key || key.toUpperCase?.() === 'TBA' || key.toUpperCase?.() === 'UNASSIGNED') continue;
      const blocks = getRowTimeBlocks(row);
      if (blocks.length === 0) continue;
      const existing = validationBooking[key] || [];
      const conflict = blocks.find(block => existing.find(slot => isOverlap(block.start, block.end, slot.start, slot.end) && slot.day === block.day));
      if (conflict) {
        const conflictSlot = existing.find(slot => isOverlap(conflict.start, conflict.end, slot.start, slot.end) && slot.day === conflict.day);
        conflicts.push({ 
          professor: row.professor, 
          courseId: row.courseId, 
          courseName: row.courseName, 
          day: row.day, 
          time: row.time,
          conflictSource: conflictSlot?.source,
          conflictSlot: conflictSlot ? `${conflictSlot.day} ${formatDecimalToClock(conflictSlot.start)}-${formatDecimalToClock(conflictSlot.end)}` : ''
        });
      } else {
        validationBooking[key] = [...existing, ...blocks.map(b => ({...b, source: 'generated'}))];
      }
    }

    if (conflicts.length > 0) {
      const entries = conflicts.map(c => {
        const sourceLabel = c.conflictSource === 'saved-schedule' ? '(from saved schedule)' : c.conflictSource === 'prof-doc-schedule' ? '(from prof doc)' : '(from another row)';
        return `${c.professor} → ${c.courseId} on ${c.conflictSlot} ${sourceLabel}`;
      });
      setStatusIsError(true);
      setStatusMessage(`Cannot save: professor unavailable - ${entries.join('; ')}`);
      console.log('Validation conflicts:', conflicts);
      return;
    }
    setIsSavingGenerated(true);
    try {
      const schedulesCollection = collection(db, 'schedules');
      const scheduleRefs = generatedSchedules.map(() => doc(schedulesCollection));
      const existingScheduleSnapshot = await getDocs(query(
        schedulesCollection,
        where('schoolYear', '==', selectedSchoolYear)
      ));
      const existingSchedules = existingScheduleSnapshot.docs.map(scheduleDoc => ({
        id: scheduleDoc.id,
        ...scheduleDoc.data()
      }));
      const roomRefsById = new Map();
      generatedSchedules.forEach(row => {
        splitScheduleSegments(row.room).forEach(roomName => {
          const room = rooms.find(candidate =>
            normalizeRoomName(candidate.roomName) === normalizeRoomName(roomName)
          );
          if (room?.id) {
            roomRefsById.set(room.id, doc(db, 'rooms', room.id));
          }
        });
      });
      const lockRefs = new Map();
      generatedSchedules.forEach(row => {
        getRoomScheduleEntries(row).forEach(entry => {
          const lockId = getRoomBookingLockId(selectedSchoolYear, selectedSemester, entry.room, entry.day);
          lockRefs.set(lockId, {
            ref: doc(db, 'roomAvailabilityLocks', lockId),
            room: entry.room,
            day: entry.day,
          });
        });
      });
      const roomValidation = await runTransaction(db, async transaction => {
        const [roomSnapshots, lockSnapshots] = await Promise.all([
          Promise.all([...roomRefsById.values()].map(roomRef => transaction.get(roomRef))),
          Promise.all([...lockRefs.entries()].map(async ([lockId, lock]) => [
            lockId,
            lock,
            await transaction.get(lock.ref)
          ]))
        ]);
        const latestRooms = roomSnapshots
          .filter(roomSnapshot => roomSnapshot.exists())
          .map(roomSnapshot => ({ id: roomSnapshot.id, ...roomSnapshot.data() }));
        const roomConflicts = getRoomBookingConflicts(generatedSchedules, existingSchedules);
        const assignmentIssues = getRoomAssignmentIssues(generatedSchedules, latestRooms);
        const lockedBookings = new Map(lockSnapshots.map(([lockId, , lockSnapshot]) => [
          lockId,
          lockSnapshot.exists() && Array.isArray(lockSnapshot.data().bookings)
            ? [...lockSnapshot.data().bookings]
            : []
        ]));
        const lockConflicts = [];
        const pendingLockBookings = new Map();
        generatedSchedules.forEach((row, rowIndex) => {
          getRoomScheduleEntries(row).forEach(entry => {
            const lockId = getRoomBookingLockId(selectedSchoolYear, selectedSemester, entry.room, entry.day);
            const bookings = [
              ...(lockedBookings.get(lockId) || []),
              ...(pendingLockBookings.get(lockId) || [])
            ];
            const conflict = bookings.find(booking =>
              isOverlap(entry.start, entry.end, booking.start, booking.end)
            );
            if (conflict) {
              lockConflicts.push(
                `${entry.room} on ${entry.day} ${formatDecimalToClock(entry.start)}-${formatDecimalToClock(entry.end)} overlaps a concurrently saved schedule (${formatDecimalToClock(conflict.start)}-${formatDecimalToClock(conflict.end)})`
              );
              return;
            }
            pendingLockBookings.set(lockId, [
              ...(pendingLockBookings.get(lockId) || []),
              {
                scheduleId: scheduleRefs[rowIndex].id,
                courseId: row.courseId,
                start: entry.start,
                end: entry.end,
              }
            ]);
          });
        });
        const validationIssues = [...roomConflicts, ...assignmentIssues, ...lockConflicts];
        if (validationIssues.length > 0) return validationIssues;

        generatedSchedules.forEach((row, index) => {
          transaction.set(scheduleRefs[index], {
            department: dep,
            curriculumYear: selectedCurriculumYear,
            schoolYear: selectedSchoolYear,
            section: selectedSection,
            semester: selectedSemester,
            yearLevel: selectedYearLevel,
            courseId: row.courseId,
            courseName: row.courseName,
            day: row.day,
            time: row.time,
            units: row.units || '',
            room: row.room || 'TBA',
            professor: row.professor,
            professorId: row.professorId || '',
            prof: row.professorEmail || row.professorId || row.professor || '',
            professorEmail: row.professorEmail || '',
            type: row.type,
          });
        });
        pendingLockBookings.forEach((bookings, lockId) => {
          const lock = lockRefs.get(lockId);
          const existingBookings = lockedBookings.get(lockId) || [];
          transaction.set(lock.ref, {
            schoolYear: selectedSchoolYear,
            semester: selectedSemester,
            roomName: lock.room,
            day: lock.day,
            bookings: [...existingBookings, ...bookings],
          });
        });
        return [];
      });
      if (roomValidation.length > 0) {
        setStatusIsError(true);
        setStatusMessage(`Cannot save: room availability changed after generation. ${roomValidation.join('; ')}`);
        return;
      }

      for (const [index, row] of generatedSchedules.entries()) {
        const newDocRef = scheduleRefs[index];
        // Attempt to append a lightweight schedule summary to the professor's user document.
        // If `professorId` is missing, try to resolve by `professorEmail`.
        try {
          let profId = row.professorId || '';
          if (!profId && row.professorEmail) {
            try {
              const usersQ = query(collection(db, 'users'), where('email', '==', row.professorEmail));
              const usersSnap = await getDocs(usersQ);
              if (!usersSnap.empty) {
                profId = usersSnap.docs[0].id;
              }
            } catch (lookupErr) {
              console.warn('Failed to lookup user by email', row.professorEmail, lookupErr.message || lookupErr);
            }
          }

          if (profId) {
            const userRef = doc(db, 'users', profId);
            try {
              await updateDoc(userRef, {
                schedules: arrayUnion({
                  scheduleId: newDocRef.id,
                  schoolYear: selectedSchoolYear,
                  semester: selectedSemester,
                  section: selectedSection,
                  yearLevel: selectedYearLevel,
                  courseId: row.courseId,
                  courseName: row.courseName,
                  day: row.day,
                  time: row.time,
                  room: row.room || 'TBA',
                }),
              });
              console.log('Appended schedule to user', profId, 'for schedule', newDocRef.id);
            } catch (updateErr) {
              // Non-fatal: log but continue saving other rows
              console.warn('Failed to update professor user doc for', profId, updateErr.message || updateErr);
            }
          } else {
            console.log('No professor user found to attach schedule for', row.professorEmail || row.professor || row.professorId);
          }
        } catch (err) {
          console.warn('Unexpected error while updating professor doc for', row.professorId || row.professorEmail, err.message || err);
        }
      }

      setStatusIsError(false);
      setStatusMessage('Generated schedule rows saved successfully.');
      setGeneratedSchedules([]);
      fetchSavedSchedules();
    } catch (err) {
      console.error(err);
      setStatusIsError(true);
      setStatusMessage('Failed to save generated schedules: ' + (err.message || 'Unknown error'));
    } finally {
      setIsSavingGenerated(false);
    }
  };

  const scheduleableCourses = useMemo(() => {
    const mapped = courses.map((course, index) => {
      const courseIdKey = normalizeCourseId(course.courseId);
      return {
        ...course,
        lectureDays: preferredDays[`${courseIdKey}_Lecture`]?.days || [],
        labDays: preferredDays[`${courseIdKey}_Laboratory`]?.days || [],
        originalIndex: index,
      };
    });

    if (isGeUser) {
      // For GE users, sort minor subjects to top, major subjects to bottom
      return mapped.slice().sort((a, b) => {
        if (a.isMinorSubject === b.isMinorSubject) {
          return a.originalIndex - b.originalIndex;
        }
        return a.isMinorSubject ? -1 : 1; // Minor first, Major at bottom
      });
    }

    // For department heads, sort major subjects to top, minor subjects to bottom
    return mapped.slice().sort((a, b) => {
      if (a.isMinorSubject === b.isMinorSubject) {
        return a.originalIndex - b.originalIndex;
      }
      return a.isMinorSubject ? 1 : -1; // Major first, Minor at bottom
    });
  }, [courses, preferredDays, isGeUser]);

  const curriculumSelectionComplete = Boolean(selectedCurriculumYear && selectedSemester && selectedYearLevel);
  const groupSelectionComplete = Boolean(selectedSchoolYear && selectedSection);
  const activeStep = !curriculumSelectionComplete ? 1 :
    !groupSelectionComplete ? 2 :
      generatedSchedules.length === 0 ? 3 :
        hasUnscheduledBlocks ? 4 : 5;
  const statusItems = statusMessage.split(/;\s+/).filter(Boolean);
  const workflowSteps = [
    {
      number: 1,
      title: 'Curriculum and academic selections',
      complete: curriculumSelectionComplete,
      detail: curriculumSelectionComplete ? 'Selections complete' : 'Choose a curriculum year, semester, and year level'
    },
    {
      number: 2,
      title: 'School year and section',
      complete: groupSelectionComplete,
      detail: groupSelectionComplete ? 'Selections complete' : 'Choose the class group to schedule'
    },
    {
      number: 3,
      title: 'Course preferences',
      complete: generatedSchedules.length > 0,
      detail: courses.length > 0 ? `${scheduleableCourses.length} course${scheduleableCourses.length === 1 ? '' : 's'} loaded` : 'Waiting for curriculum selections'
    },
    {
      number: 4,
      title: 'Generated schedule preview',
      complete: generatedSchedules.length > 0 && !hasUnscheduledBlocks,
      detail: generatedSchedules.length > 0 ? `${generatedSchedules.length} preview row${generatedSchedules.length === 1 ? '' : 's'}` : 'Generate a schedule to review the preview'
    },
    {
      number: 5,
      title: 'Save generated schedule',
      complete: false,
      detail: generatedSchedules.length > 0 && !hasUnscheduledBlocks ? 'Preview is ready to save' : 'Available after reviewing a complete preview'
    }
  ];

  return (
    <>
    <div className="scheduling-page">
      <header className="scheduling-page-header">
        <div>
          <p className="scheduling-eyebrow">PROGRAM HEAD</p>
          <h2>Scheduling Management</h2>
          <p>Set up a class group, choose course days, review the generated schedule, and save it.</p>
        </div>
      </header>
      {isGeUser && (
        <div style={{ background: '#fff', borderRadius: 14, padding: 20, border: '1px solid #e1e8ed', marginBottom: 24 }}>
          <div style={{ fontSize: 18, fontWeight: 700, marginBottom: 12, color: '#155c2b' }}>GE To-Do: Missing minor schedules</div>
          {todoItems.length === 0 ? (
            <div style={{ color: '#667f92', fontSize: 14 }}>No department missing minor schedules found. All existing groups have minor subject coverage.</div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              <div style={{ marginBottom: 8, color: '#444', fontSize: 13 }}>Select a group to autofill year, semester, school year, and section before generating schedules.</div>
              <div style={{ display: 'grid', gap: 12 }}>
                {todoItems.map(item => (
                  <div key={item.id} style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: 12, padding: 14, border: '1px solid #e6eaf0', borderRadius: 12, background: '#f9fbfc', alignItems: 'center' }}>
                    <label style={{ display: 'flex', alignItems: 'center', gap: 10, width: '100%' }}>
                      <input type="radio" name="geTodoGroup" checked={selectedTodos.includes(item.id)} onChange={() => toggleTodo(item)} />
                      <div style={{ fontSize: 13, width: '100%' }}>
                        <div style={{ fontWeight: 700, color: '#22303c' }}>{item.curriculumYear} • {item.schoolYear} • {item.semester} • {item.yearLevel} • Section {item.section}</div>
                        <div style={{ fontSize: 12, color: '#666' }}>{item.missingCount} minor subject(s) missing</div>
                      </div>
                    </label>
                    <button type="button" onClick={() => selectTodoItem(item)} style={{ ...actionButtonStyle, background: '#0f6f2b', padding: '10px 16px', fontSize: 13 }}>Select</button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      <nav className="scheduling-workflow" aria-label="Schedule creation steps">
        {workflowSteps.map(step => {
          const current = activeStep === step.number;
          const stateLabel = step.complete ? 'Complete' : current ? 'Current step' : 'Needs attention';
          return (
            <div
              key={step.number}
              className={`scheduling-workflow-step${current ? ' is-current' : ''}${step.complete ? ' is-complete' : ''}`}
              aria-current={current ? 'step' : undefined}
            >
              <span className="scheduling-step-number">{step.complete ? '✓' : step.number}</span>
              <div className="scheduling-step-copy">
                <span className="scheduling-step-state">{stateLabel}</span>
                <strong>{step.title}</strong>
                <span>{step.detail}</span>
              </div>
            </div>
          );
        })}
      </nav>

      <section className="scheduling-section" aria-labelledby="scheduling-academic-heading">
        <div className="scheduling-section-heading">
          <span className="scheduling-section-number">1</span>
          <div>
            <h3 id="scheduling-academic-heading">Curriculum and academic selections</h3>
            <p>Choose the curriculum and term for the courses you want to schedule.</p>
          </div>
        </div>
        <div className="scheduling-academic-grid">
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          <label style={{ fontWeight: 700, color: '#22303c' }}>Department</label>
          {allDepartments && allDepartments.length > 0 ? (
            <select value={selectedDepartment} onChange={e => setSelectedDepartment(e.target.value)} style={controlStyle}>
              {allDepartments.map(dep => <option key={dep} value={dep}>{dep}</option>)}
            </select>
          ) : (
            <input readOnly value={normalizeDepartment(department, selectedDepartment)} style={{ ...controlStyle, background: '#f7f9fb' }} />
          )}
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          <label style={{ fontWeight: 700, color: '#22303c' }}>Curriculum Year</label>
          <select value={selectedCurriculumYear} onChange={e => setSelectedCurriculumYear(e.target.value)} style={controlStyle}>
            <option value="">Select Year</option>
            {curriculumYears.map(year => <option key={year} value={year}>{year}</option>)}
          </select>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          <label style={{ fontWeight: 700, color: '#22303c' }}>Semester</label>
          <select value={selectedSemester} onChange={e => setSelectedSemester(e.target.value)} style={controlStyle}>
            <option>First Semester</option>
            <option>Second Semester</option>
          </select>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          <label style={{ fontWeight: 700, color: '#22303c' }}>Year Level</label>
          <select value={selectedYearLevel} onChange={e => setSelectedYearLevel(e.target.value)} style={controlStyle}>
            <option value="">Select Year Level</option>
            {availableYearLevels.map(level => <option key={level} value={level}>{level}</option>)}
          </select>
        </div>
        </div>
      </section>

      <section className="scheduling-section" aria-labelledby="scheduling-group-heading">
        <div className="scheduling-section-heading">
          <span className="scheduling-section-number">2</span>
          <div>
            <h3 id="scheduling-group-heading">School year and section</h3>
            <p>Select the class group to schedule. New options are available for this session.</p>
          </div>
        </div>
        <div className="scheduling-group-grid">
          <div className="scheduling-group-field">
            <label style={{ fontWeight: 700, color: '#22303c' }}>School Year</label>
            <select value={selectedSchoolYear} onChange={e => setSelectedSchoolYear(e.target.value)} style={controlStyle}>
              <option value="">Select School Year</option>
              {displaySchoolYears.map(year => <option key={year} value={year}>{year}</option>)}
            </select>
          </div>

          {!isGeUser && (
            <div className="scheduling-group-field">
              <label style={{ fontWeight: 700, color: '#22303c' }}>Add School Year</label>
              <div className="scheduling-inline-add">
                <input value={newSchoolYear} onChange={e => setNewSchoolYear(e.target.value)} placeholder="e.g. 2026-2027" style={controlStyle} />
                <button type="button" onClick={() => {
                  const normalized = newSchoolYear.trim().replace(/\s*-\s*/g, '-');
                  if (!normalized) return;
                  const yearRegex = /^\d{4}-\d{4}$/;
                  if (!yearRegex.test(normalized)) {
                    setStatusMessage('School year must use YYYY-YYYY format.');
                    return;
                  }
                  if (schoolYears.includes(normalized)) {
                    setStatusMessage(`${normalized} already exists.`);
                    return;
                  }
                  setSchoolYears(prev => [...prev, normalized]);
                  setNewSchoolYear('');
                  setStatusMessage(`Added school year ${normalized}.`);
                }} style={primaryButtonStyle}>Add</button>
              </div>
            </div>
          )}

          <div className="scheduling-group-field">
            <label style={{ fontWeight: 700, color: '#22303c' }}>Section</label>
            <select value={selectedSection} onChange={e => setSelectedSection(e.target.value)} style={controlStyle}>
              <option value="">Select Section</option>
              {displaySections.map(section => <option key={section} value={section}>{section}</option>)}
            </select>
          </div>

          {isGeUser ? (
            <div className="scheduling-group-guidance">
              Use the GE To-Do list above to select a department group and autofill year, semester, school year, and section.
            </div>
          ) : (
            <div className="scheduling-group-field">
              <label style={{ fontWeight: 700, color: '#22303c' }}>Add Section</label>
              <div className="scheduling-inline-add">
                <input value={newSection} onChange={e => setNewSection(e.target.value)} placeholder="Section" style={controlStyle} />
                <button type="button" onClick={() => {
                  if (!newSection) return;
                  if (sections.includes(newSection)) {
                    setStatusMessage(`${newSection} already exists.`);
                    return;
                  }
                  setSections(prev => [...prev, newSection]);
                  setNewSection('');
                  setStatusMessage(`Added section ${newSection}.`);
                }} style={primaryButtonStyle}>Add</button>
              </div>
            </div>
          )}
        </div>
      </section>

      <section className="scheduling-section" aria-labelledby="scheduling-preferences-heading">
        <div className="scheduling-section-heading">
          <span className="scheduling-section-number">3</span>
          <div>
            <h3 id="scheduling-preferences-heading">Course preferences</h3>
            <p>Choose preferred weekdays for each lecture and laboratory. Selected days continue to guide schedule generation.</p>
          </div>
        </div>
        {(roomsLoading || roomsLoadError || roomBookingsLoading || roomBookingsLoadError) && (
          <div
            className={`scheduling-status${roomsLoadError || roomBookingsLoadError ? ' is-error' : ' is-info'}`}
            role={roomsLoadError || roomBookingsLoadError ? 'alert' : 'status'}
          >
            <div>
              <h3>{roomsLoadError || roomBookingsLoadError ? 'Room availability cannot be verified' : 'Loading room availability'}</h3>
              <p>
                {roomsLoadError || roomBookingsLoadError || 'Rooms and existing bookings are being refreshed. Schedule generation will be available when loading completes.'}
              </p>
            </div>
          </div>
        )}
        <div className="scheduling-preference-toolbar">
          <div>
            <h4>Available Curriculum Subjects</h4>
            <p>Weekday choices are shown separately for each course meeting type.</p>
          </div>
        </div>
        {scheduleableCourses.length === 0 ? (
          <div className="scheduling-empty-state">
            {isGeUser ? 'No minor subjects found for this department and selected filters.' : 'Select curriculum filters to view subjects.'}
          </div>
        ) : (
          <div className="scheduling-course-list">
            {scheduleableCourses.map(course => (
              <article className="scheduling-course-card" key={course.id}>
                <div className="scheduling-course-heading">
                  <div>
                    <span className="scheduling-course-id">{course.courseId}</span>
                    <h4>{course.courseName}</h4>
                  </div>
                  <span className={`scheduling-course-badge${course.isMinorSubject ? ' is-minor' : ''}`}>
                    {course.isMinorSubject ? 'Minor subject' : (isGeUser ? 'Major subject · view only' : 'Major subject')}
                  </span>
                </div>
                <div className="scheduling-course-meta" aria-label={`${course.courseName} course information`}>
                  <span><strong>Lecture</strong>{course.lectureHours || '—'} hrs</span>
                  <span><strong>Laboratory</strong>{course.labHours || '—'} hrs</span>
                  <span><strong>Training</strong>{course.trainingHours || '—'} hrs</span>
                </div>
                <div className="scheduling-course-preferences">
                  {course.lectureHours ? (
                    <fieldset className="scheduling-day-group">
                      <legend>Lecture preferred days</legend>
                      <div className="scheduling-day-options">
                        {dayOptions.map(day => (
                          <label className="scheduling-day-option" key={`${course.courseId}-lec-${day}`} title={day}>
                            <input
                              type="checkbox"
                              aria-label={`${day} lecture preferred day for ${course.courseId}`}
                              checked={course.lectureDays.includes(day)}
                              disabled={isGeUser && !course.isMinorSubject}
                              onChange={() => !isGeUser || course.isMinorSubject ? togglePreferredDay(course.courseId, 'Lecture', day) : null}
                              style={isGeUser && !course.isMinorSubject ? { cursor: 'not-allowed', accentColor: '#f39c12', color: '#f39c12', border: '2px solid #f39c12', transform: 'scale(1.1)', marginTop: 1 } : { accentColor: '#0f6f2b', color: '#0f6f2b', border: '2px solid #0f6f2b', transform: 'scale(1.05)', marginTop: 1 }}
                            />
                            {day.slice(0, 3)}
                          </label>
                        ))}
                      </div>
                    </fieldset>
                  ) : null}
                  {course.labHours ? (
                    <fieldset className="scheduling-day-group">
                      <legend>Laboratory preferred days</legend>
                      <div className="scheduling-day-options">
                        {dayOptions.map(day => (
                          <label className="scheduling-day-option" key={`${course.courseId}-lab-${day}`} title={day}>
                            <input
                              type="checkbox"
                              aria-label={`${day} laboratory preferred day for ${course.courseId}`}
                              checked={course.labDays.includes(day)}
                              disabled={isGeUser && !course.isMinorSubject}
                              onChange={() => !isGeUser || course.isMinorSubject ? togglePreferredDay(course.courseId, 'Laboratory', day) : null}
                              style={isGeUser && !course.isMinorSubject ? { cursor: 'not-allowed', accentColor: '#f39c12', color: '#f39c12', border: '2px solid #f39c12', transform: 'scale(1.1)', marginTop: 1 } : { accentColor: '#0f6f2b', color: '#0f6f2b', border: '2px solid #0f6f2b', transform: 'scale(1.05)', marginTop: 1 }}
                            />
                            {day.slice(0, 3)}
                          </label>
                        ))}
                      </div>
                    </fieldset>
                  ) : null}
                  {!course.lectureHours && !course.labHours ? (
                    <span className="scheduling-no-meetings">No lecture/lab hours</span>
                  ) : null}
                  {!course.isMinorSubject && (course.lectureDays.length > 0 || course.labDays.length > 0) ? (
                    <div className="scheduling-department-preferences">Department preferred days</div>
                  ) : null}
                </div>
              </article>
            ))}
          </div>
        )}
        <div className="scheduling-action-row">
          <button
            type="button"
            onClick={handleGenerateSchedule}
            style={roomsLoading || roomsLoadError || roomBookingsLoading || roomBookingsLoadError || roomBookingSchoolYear !== selectedSchoolYear ? disabledButtonStyle : primaryButtonStyle}
            disabled={roomsLoading || Boolean(roomsLoadError) || roomBookingsLoading || Boolean(roomBookingsLoadError) || roomBookingSchoolYear !== selectedSchoolYear}
          >
            Generate Schedule
          </button>
        </div>
      </section>

      {statusMessage && (
        <section
          className={`scheduling-status${statusIsError || hasUnscheduledBlocks ? ' is-error' : ' is-info'}`}
          role={statusIsError || hasUnscheduledBlocks ? 'alert' : 'status'}
          aria-live={statusIsError || hasUnscheduledBlocks ? 'assertive' : 'polite'}
        >
          <div>
            <h3>{statusIsError || hasUnscheduledBlocks ? 'Scheduling needs attention' : 'Scheduling update'}</h3>
            <ul>
              {statusItems.map((item, index) => <li key={`${index}-${item}`}>{item}{index < statusItems.length - 1 ? ';' : ''}</li>)}
            </ul>
          </div>
          <button
            type="button"
            className="scheduling-status-close"
            aria-label="Dismiss scheduling message"
            onClick={() => { setStatusMessage(''); setStatusIsError(false); }}
          >
            ×
          </button>
        </section>
      )}

      <section className="scheduling-section" aria-labelledby="scheduling-preview-heading">
        <div className="scheduling-section-heading">
          <span className="scheduling-section-number">4</span>
          <div>
            <h3 id="scheduling-preview-heading">Generated schedule preview</h3>
            <p>Review generated times, rooms, and professor assignments before saving.</p>
          </div>
          {generatedSchedules.length > 0 && <span className="scheduling-preview-count">{generatedSchedules.length} rows</span>}
        </div>
        <div className="scheduling-table-scroll">
          <table className="scheduling-table scheduling-preview-table">
            <thead>
              <tr style={{ background: '#f3f6f9', textAlign: 'left' }}>
                <th style={{ padding: 12, border: '1px solid #dde2e6', width: 130, minWidth: 130 }}>Year Level</th>
                <th style={{ padding: 12, border: '1px solid #dde2e6', width: 120, minWidth: 120 }}>Semester</th>
                <th style={{ padding: 12, border: '1px solid #dde2e6', width: 90, minWidth: 90 }}>Section</th>
                <th style={{ padding: 12, border: '1px solid #dde2e6', width: 130, minWidth: 130 }}>Course ID</th>
                <th style={{ padding: 12, border: '1px solid #dde2e6', width: 320, minWidth: 320 }}>Course Name</th>
                <th style={{ padding: 12, border: '1px solid #dde2e6', width: 220, minWidth: 220 }}>Day</th>
                <th style={{ padding: 12, border: '1px solid #dde2e6', width: 220, minWidth: 220 }}>Time</th>
                <th style={{ padding: 12, border: '1px solid #dde2e6', width: 140, minWidth: 140 }}>Room</th>
                <th style={{ padding: 12, border: '1px solid #dde2e6', width: 160, minWidth: 160 }}>Professor</th>
                <th style={{ padding: 12, border: '1px solid #dde2e6', width: 110, minWidth: 110 }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {generatedSchedules.length === 0 ? (
                <tr>
                  <td colSpan={9} style={{ padding: 24, textAlign: 'center', color: '#667f92' }}>
                    Generated schedule rows will appear here after you click Generate Schedule.
                  </td>
                </tr>
              ) : generatedSchedules.map(row => (
                <tr key={row.id} style={{ background: '#fff' }}>
                  <td style={{ padding: 12, border: '1px solid #eef2f6', whiteSpace: 'normal', wordBreak: 'break-word' }}>{row.yearLevel}</td>
                  <td style={{ padding: 12, border: '1px solid #eef2f6', whiteSpace: 'normal', wordBreak: 'break-word' }}>{row.semester}</td>
                  <td style={{ padding: 12, border: '1px solid #eef2f6', whiteSpace: 'normal', wordBreak: 'break-word' }}>{row.section}</td>
                  <td style={{ padding: 12, border: '1px solid #eef2f6', whiteSpace: 'normal', wordBreak: 'break-word' }}>{row.courseId}</td>
                  <td style={{ padding: 12, border: '1px solid #eef2f6', whiteSpace: 'normal', wordBreak: 'break-word', minWidth: 320 }}>{row.courseName}</td>
                  <td style={{ padding: 12, border: '1px solid #eef2f6', whiteSpace: 'pre-line', wordBreak: 'break-word', minWidth: 220 }}>{row.day}</td>
                  <td style={{ padding: 12, border: '1px solid #eef2f6', whiteSpace: 'pre-line', wordBreak: 'break-word', minWidth: 220 }}>{row.time}</td>
                  <td style={{ padding: 12, border: '1px solid #eef2f6', whiteSpace: 'pre-line', wordBreak: 'break-word', minWidth: 140 }}>{row.room || 'TBA'}</td>
                  <td style={{ padding: 12, border: '1px solid #eef2f6' }}>
                    <select value={row.professor} onChange={e => handleProfessorChange(row.id, e.target.value)} style={{ ...controlStyle, width: '100%' }}>
                      {getProfessorOptionsForRow(row).map(option => (
                        <option key={option.profId || option.value} value={option.value} disabled={option.disabled}>{option.label}</option>
                      ))}
                    </select>
                  </td>
                  <td style={{ padding: 12, border: '1px solid #eef2f6', textAlign: 'center' }}>
                    <button type="button" onClick={() => setGeneratedRowToRemove(row)} style={{ ...actionButtonStyle, background: '#dc3545' }}>Remove</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <section className="scheduling-save-section" aria-labelledby="scheduling-save-heading">
        <div>
          <span className="scheduling-save-step">STEP 5</span>
          <h3 id="scheduling-save-heading">Save generated schedule</h3>
          <p>{hasUnscheduledBlocks ? 'Resolve unscheduled blocks in the preview before saving.' : 'Save the reviewed preview to the department schedule.'}</p>
        </div>
        <button
          type="button"
          onClick={handleSaveGenerated}
          style={hasUnscheduledBlocks || generatedSchedules.length === 0 || isSavingGenerated ? disabledButtonStyle : secondaryButtonStyle}
          disabled={hasUnscheduledBlocks || generatedSchedules.length === 0 || isSavingGenerated}
          aria-busy={isSavingGenerated}
        >
          {isSavingGenerated && <span className="scheduling-save-spinner" aria-hidden="true" />}
          {isSavingGenerated ? 'Saving schedule...' : 'Save Generated Schedule'}
        </button>
      </section>

      <section className="scheduling-section scheduling-saved-section" aria-labelledby="scheduling-saved-heading">
        <div className="scheduling-section-heading">
          <span className="scheduling-saved-icon" aria-hidden="true">▤</span>
          <div>
            <h3 id="scheduling-saved-heading">Saved schedules</h3>
            <p>Browse and manage schedules already saved for this department.</p>
          </div>
          <span className="scheduling-preview-count">{filteredSavedSchedules.length} shown</span>
        </div>
        <div className="scheduling-saved-filters">
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            <label style={{ fontWeight: 700, color: '#22303c' }}>Filter by year level</label>
            <select value={savedFilterYearLevel} onChange={e => setSavedFilterYearLevel(e.target.value)} style={controlStyle}>
              <option value="">All Year Levels</option>
              {savedYearLevels.map(level => <option key={level} value={level}>{level}</option>)}
            </select>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            <label style={{ fontWeight: 700, color: '#22303c' }}>Filter by semester</label>
            <select value={savedFilterSemester} onChange={e => setSavedFilterSemester(e.target.value)} style={controlStyle}>
              <option value="">All Semesters</option>
              {savedSemesters.map(semester => <option key={semester} value={semester}>{semester}</option>)}
            </select>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            <label style={{ fontWeight: 700, color: '#22303c' }}>Filter by section</label>
            <select value={savedFilterSection} onChange={e => setSavedFilterSection(e.target.value)} style={controlStyle}>
              <option value="">All Sections</option>
              {savedSections.map(section => <option key={section} value={section}>{section}</option>)}
            </select>
          </div>
        </div>
        <div className="scheduling-table-scroll">
          <table className="scheduling-table scheduling-saved-table">
            <thead>
              <tr style={{ background: '#f3f6f9', textAlign: 'left' }}>
                <th style={{ padding: 12, border: '1px solid #dde2e6', minWidth: 130 }}>Year Level</th>
                <th style={{ padding: 12, border: '1px solid #dde2e6', minWidth: 110 }}>Semester</th>
                <th style={{ padding: 12, border: '1px solid #dde2e6', minWidth: 90 }}>Section</th>
                <th style={{ padding: 12, border: '1px solid #dde2e6', minWidth: 130 }}>Course ID</th>
                <th style={{ padding: 12, border: '1px solid #dde2e6', minWidth: 320 }}>Course Name</th>
                <th style={{ padding: 12, border: '1px solid #dde2e6', minWidth: 220 }}>Day</th>
                <th style={{ padding: 12, border: '1px solid #dde2e6', minWidth: 220 }}>Time</th>
                <th style={{ padding: 12, border: '1px solid #dde2e6', minWidth: 80 }}>Units</th>
                <th style={{ padding: 12, border: '1px solid #dde2e6', minWidth: 120 }}>Room</th>
                <th style={{ padding: 12, border: '1px solid #dde2e6', minWidth: 130 }}>Professor</th>
                <th style={{ padding: 12, border: '1px solid #dde2e6', minWidth: 110 }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredSavedSchedules.length === 0 ? (
                <tr>
                  <td colSpan={10} style={{ padding: 24, textAlign: 'center', color: '#667f92' }}>
                    {savedSchedules.length === 0 ? 'No saved schedules yet. Saved schedules will appear here after you click Save Generated Schedule.' : 'No saved schedules match the selected filters.'}
                  </td>
                </tr>
              ) : filteredSavedSchedules.map(row => (
                <tr key={row.id} style={{ background: '#fff' }}>
                  <td style={{ padding: 12, border: '1px solid #eef2f6' }}>{row.yearLevel}</td>
                  <td style={{ padding: 12, border: '1px solid #eef2f6' }}>{row.semester}</td>
                  <td style={{ padding: 12, border: '1px solid #eef2f6' }}>{row.section}</td>
                  <td style={{ padding: 12, border: '1px solid #eef2f6' }}>{row.courseId}</td>
                  <td style={{ padding: 12, border: '1px solid #eef2f6' }}>{row.courseName}</td>
                  <td style={{ padding: 12, border: '1px solid #eef2f6', whiteSpace: 'pre-line', wordBreak: 'break-word', minWidth: 220 }}>{row.day}</td>
                  <td style={{ padding: 12, border: '1px solid #eef2f6', whiteSpace: 'pre-line', wordBreak: 'break-word', minWidth: 220 }}>{row.time}</td>
                  <td style={{ padding: 12, border: '1px solid #eef2f6' }}>{row.units || '-'}</td>
                  <td style={{ padding: 12, border: '1px solid #eef2f6', whiteSpace: 'pre-line', wordBreak: 'break-word', minWidth: 120 }}>{row.room || 'TBA'}</td>
                  <td style={{ padding: 12, border: '1px solid #eef2f6' }}>{row.professor || 'TBA'}</td>
                  <td style={{ padding: 12, border: '1px solid #eef2f6', textAlign: 'center' }}>
                    <button type="button" onClick={() => { setStatusMessage(''); setScheduleToDelete(row); }} style={{ ...actionButtonStyle, background: '#dc3545' }}>Delete</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
    </div>
    <DeleteConfirmationModal
      open={Boolean(scheduleToDelete)}
      title="Delete Schedule?"
      subtitle="Confirm schedule deletion"
      name={scheduleToDelete?.courseName || "this schedule"}
      message={`Are you sure you want to delete the saved schedule for ${scheduleToDelete?.courseName || "this course"}? This action cannot be undone.`}
      detail="Please verify the schedule details before proceeding."
      error={statusIsError ? statusMessage : ""}
      confirmLabel="Delete Schedule"
      onCancel={() => { setScheduleToDelete(null); setStatusMessage(''); setStatusIsError(false); }}
      onConfirm={handleDeleteSavedSchedule}
    />
    <DeleteConfirmationModal
      open={Boolean(generatedRowToRemove)}
      title="Remove Generated Schedule?"
      subtitle="Confirm schedule row removal"
      name={generatedRowToRemove?.courseName || "this schedule row"}
      message={`Are you sure you want to remove ${generatedRowToRemove?.courseName || "this schedule row"} from the generated schedule? This change has not been saved.`}
      detail="Please verify the schedule details before proceeding."
      confirmLabel="Remove Row"
      onCancel={() => setGeneratedRowToRemove(null)}
      onConfirm={handleRemoveGeneratedRow}
    />
    </>
  );
}
